import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { usePopupPrefill } from "../ai-chat/aiPopupGateway";
import { FormControl } from "react-bootstrap";
import {
  LuChevronDown,
  LuChevronUp,
  LuCheck,
  LuInfo,
  LuPause,
  LuPlay,
  LuSettings,
  LuTrash2,
  LuUndo2,
} from "react-icons/lu";
import i18n from "i18next";
import { getToken, getOrganizationId } from "../../../common/environment_config.es6";
import { waitForTtsJob } from "../../../common/tts_job_poll.es6";
import { approveJob } from "../../../common/server_utils.es6";
import { withSidecarSuffix } from "../../../common/utils.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import SceneAudioService from "../../services/sceneAudioService";
import {
  getNarrationDraft,
  setNarrationDraft,
  replaceNarrationDraft,
  clearNarrationDraft,
  subscribeNarrationDrafts,
  useNarrationDraft,
  buildNarrationSoundData,
} from "./narrationDraftStore";
import { getMediaDuration } from "../../utils/mediaDuration";
import NarrateAllConfirmModal from "./NarrateAllConfirmModal";
import DeleteAvatarConfirmModal from "./DeleteAvatarConfirmModal";
import { getEditorPlayhead } from "../../../common/playhead_tracker.es6";
import { isCreditRefusal } from "../../utils/insufficientBalance";
import { loadSlideNotes, noteForScene } from "./slideNotes";

// AI provider voices the batch narrate-all-scenes API accepts. Cloned ("own")
// voices are also supported — they go through with provider "private" + the
// clone voiceId (same contract as single-scene clone TTS), handled separately.
const BATCH_NARRATION_PROVIDERS = ["azure", "elevenlabs", "google"];

// URL'den sorgu parametresiz dosya adı (taslak sesi ↔ soundList eşleştirmesi).
const fileBaseName = (p) => (p ? String(p).split("/").pop().split("?")[0] : "");

const SERVICE_MAP = {
  microsoft: "azure",
  azure: "azure",
  elevenlabs: "elevenlabs",
  gemini: "google",
  google: "google",
  sestek: "sestek",
};
const API_SERVICE_MAP = {
  azure: "microsoft",
  elevenlabs: "elevenlabs",
  google: "gemini",
  sestek: "sestek",
};

const SERVICE_LABELS = {
  elevenlabs: "ElevenLabs",
  azure: "Azure",
  microsoft: "Azure",
  google: "Google",
  gemini: "Google",
  sestek: "Sestek",
  aidea: "Aidea",
};

// Ses cinsiyetini ("Male"/"female"/"M"/0 vb.) normalize eder (AvatarNarrationTab ile aynı).
const normalizeGender = (g) => {
  const v = String(g ?? "")
    .toLowerCase()
    .trim();
  if (v === "male" || v === "m" || v === "0" || v === "erkek") return "male";
  if (v === "female" || v === "f" || v === "1" || v === "kadın" || v === "kadin") return "female";
  return "";
};

// Ses kartı / seçili anlatıcı avatarının cinsiyete göre rengi (AvatarNarrationTab ile
// aynı): erkek mavi, kadın pembe, bilinmeyen nötr mor. bg = zemin, color = harf/ikon.
const getGenderColors = (gender) => {
  const g = normalizeGender(gender);
  if (g === "male") return { bg: "#e0f2fe", color: "#2563eb" };
  if (g === "female") return { bg: "#ffe4f0", color: "#db2777" };
  return { bg: "#ede9fe", color: "#7c3aed" };
};
const genderAvatarStyle = (gender) => {
  const c = getGenderColors(gender);
  return { background: c.bg, color: c.color };
};

// Bir ses objesinin sağlayıcısını (azure | elevenlabs | google) service/servicename
// alanından türetir. "Tümü" (All) filtresinde seçilen sesin gerçek sağlayıcısını
// selectedService'e yazmak için kullanılır — üretim bu provider ile yapıldığından şart.
const voiceToService = (voice) => {
  const raw = String(voice?.service ?? voice?.provider ?? voice?.servicename ?? "").toLowerCase();
  if (raw.includes("eleven")) return "elevenlabs";
  if (raw.includes("google") || raw.includes("gemini")) return "google";
  if (raw.includes("azure") || raw.includes("microsoft")) return "azure";
  if (raw.includes("sestek")) return "sestek";
  return "";
};

// voiceName bazen ham provider id'sinin kendisi olabilir (eski batch kayıtları /
// NarrationJobTracker'ın id fallback'i). Ham id bir görünen ad DEĞİLDİR: gerçek
// ad değilse "" döndür. Çağıran `|| fallback` ile ne göstereceğine karar verir:
//  - erken-set'te fallback yok → "-" görünür (id flicker'ı olmaz, asıl ad geliyor),
//  - matched'te `|| matchedVoice.name` → canlı API adı,
//  - eşleşme hiç yoksa `|| voiceId` → gerçek ad hiç yoksa son çare id.
const realVoiceName = (name, voiceId) => (name && name !== voiceId ? name : "");

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// Provider bazlı konuşma hızları (karakter/saniye) — ampirik kalibrasyonla TR/EN için aralık üretir.
const PROVIDER_SPEECH_RATES = {
  azure: { minCps: 13.5, maxCps: 16.5, pausePerSentence: 0.35 },
  elevenlabs: { minCps: 12.0, maxCps: 15.0, pausePerSentence: 0.5 },
  gemini: { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
  "gemini-pro": { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
  "gemini-flash-3": { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
  google: { minCps: 12.5, maxCps: 15.5, pausePerSentence: 0.45 },
  microsoft: { minCps: 13.5, maxCps: 16.5, pausePerSentence: 0.35 },
  sestek: { minCps: 13.5, maxCps: 16.5, pausePerSentence: 0.35 },
};

const GOOGLE_MODEL_OPTIONS = [
  { value: "gemini", label: "Gemini 2.5" },
  { value: "gemini-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-flash-3", label: "Gemini 3 Flash" },
];
const DEFAULT_GOOGLE_MODEL = "gemini-flash-3";

// ElevenLabs model ailesi. Provider seçiminde iki ayrı entry olarak listelenir.
//   v2_5 → turbo v2.5: backend course dilini language_code olarak zorlar
//          (aksanı o dile çeker). v3 → eleven_v3: ekspresif, language_code yok.
// Seçim üretimde `elevenLabsModel` olarak backend'e gider; dil backend'de
// training kaydından okunur (burada dil seçtirilmez).
const ELEVENLABS_MODEL_OPTIONS = [
  { value: "v2_5", label: "ElevenLabs 2.5" },
  { value: "v3", label: "ElevenLabs 3" },
];
const DEFAULT_ELEVENLABS_MODEL = "v3";

const GEMINI_TTS_STYLE_OPTIONS = [
  { value: "Vocal", labelKey: "GeminiStyle.Vocal", fallback: "Vokal" },
  { value: "Smile", labelKey: "GeminiStyle.Smile", fallback: "Gülümseyen" },
  { value: "Newscaster", labelKey: "GeminiStyle.Newscaster", fallback: "Haber Spikeri" },
  { value: "Whisper", labelKey: "GeminiStyle.Whisper", fallback: "Fısıltı" },
  { value: "Empathetic", labelKey: "GeminiStyle.Empathetic", fallback: "Empatik" },
  { value: "Promo/Hype", labelKey: "GeminiStyle.PromoHype", fallback: "Tanıtım / Heyecan" },
  { value: "Deadpan", labelKey: "GeminiStyle.Deadpan", fallback: "İfadesiz" },
];

const GEMINI_TTS_PACE_OPTIONS = [
  { value: "natural", labelKey: "GeminiPace.Natural", fallback: "Doğal" },
  { value: "rapid", labelKey: "GeminiPace.Rapid", fallback: "Hızlı" },
  { value: "fire", labelKey: "GeminiPace.Fire", fallback: "Ateşli" },
  { value: "drift", labelKey: "GeminiPace.Drift", fallback: "Akıcı" },
  { value: "staccato", labelKey: "GeminiPace.Staccato", fallback: "Kesikli" },
];

// Editörün o anki UI dilini (i18n.language: "tr"/"en"/"es"...) örnek ses
// dosyalarının dil kodu adaylarına çevirir. Örnek ses, açık olan editör diline
// göre çalsın diye handleSamplePlay'de en yüksek öncelikte kullanılır.
const editorLanguageSampleCandidates = () => {
  let stored = "";
  try {
    stored = localStorage.getItem("i18nextLng") || "";
  } catch (e) {
    stored = "";
  }
  const base = String(i18n.language || stored || "")
    .toLowerCase()
    .split("-")[0];
  const map = {
    tr: ["tr-TR", "tr"],
    en: ["en-US", "en-GB", "en"],
    es: ["es-ES", "es"],
    de: ["de-DE", "de"],
    fr: ["fr-FR", "fr"],
    ja: ["ja-JP", "ja"],
    zh: ["zh-CN", "zh"],
    az: ["az-AZ", "az"],
    pt: ["pt-BR", "pt"],
    ar: ["ar-SA", "ar"],
  };
  return map[base] || (base ? [base] : []);
};

const translateOrFallback = (key, fallback) => {
  const translated = i18n.t(key);
  return !translated || translated === key ? fallback : translated;
};

const GEMINI_FLASH3_DIRECTIVES = [
  {
    icon: "🎭",
    typeKey: "GeminiDirective.Emotion.type",
    type: "Duygu",
    tag: "[excited]",
    effectKey: "GeminiDirective.Emotion.effect",
    effect: "Sesi enerjik ve neşeli yapar.",
    example: "[excited] Harika bir gün!",
  },
  {
    icon: "🤫",
    typeKey: "GeminiDirective.Style.type",
    type: "Stil",
    tag: "[whispering]",
    effectKey: "GeminiDirective.Style.effect",
    effect: "Sesi gizemli bir fısıltıya dönüştürür.",
    example: "[whispering] Kimse duymasın.",
  },
  {
    icon: "⏱️",
    typeKey: "GeminiDirective.Time.type",
    type: "Zaman",
    tag: "[pause: 1s]",
    effectKey: "GeminiDirective.Time.effect",
    effect: "Belirtilen sürede doğal duraklama ekler.",
    example: "Hazır mısın? [pause: 1s] Başlıyoruz!",
  },
  {
    icon: "🏃",
    typeKey: "GeminiDirective.Speed.type",
    type: "Hız",
    tag: "[speed: fast]",
    effectKey: "GeminiDirective.Speed.effect",
    effect: "Konuşmayı hızlandırır (Önemli uyarılar vb.).",
    example: "[speed: fast] Acele etmelisin!",
  },
  {
    icon: "🎼",
    typeKey: "GeminiDirective.Pitch.type",
    type: "Perde",
    tag: "[pitch: low]",
    effectKey: "GeminiDirective.Pitch.effect",
    effect: "Sesi kalınlaştırıp daha otoriter yapar.",
    example: "[pitch: low] Bu çok ciddi bir konu.",
  },
  {
    icon: "📍",
    typeKey: "GeminiDirective.Scene.type",
    type: "Sahne",
    tag: "[scene: studio]",
    effectKey: "GeminiDirective.Scene.effect",
    effect: "Yankısız, pürüzsüz stüdyo ortamı sağlar.",
    example: "[scene: studio] Kayıt başlıyor.",
  },
];

const GEMINI_FLASH3_EXAMPLE_TEXT =
  "[scene: studio] [excited] Merhaba millet! [pause: 500ms] Bugün size çok özel bir sır vereceğim. [whispering] Aslında ben bir yapay zekayım. [pause: 1s] [normal] Ama [speed: fast] o kadar hızlı öğrenebilirim ki [speed: slow] sizinle gerçek bir insan gibi sohbet edebilirim.";

const estimateSpeechDuration = (text, providerKey) => {
  if (!text || !text.trim()) return 0;
  const conf = PROVIDER_SPEECH_RATES[providerKey] || PROVIDER_SPEECH_RATES.azure;
  const cleaned = text.replace(/<[^>]+>/g, "").trim();
  const charCount = cleaned.length;
  if (charCount === 0) return 0;
  const sentenceCount = (cleaned.match(/[.!?…]+/g) || []).length || 1;
  const pauseTotal = sentenceCount * conf.pausePerSentence;
  const avgCps = (conf.minCps + conf.maxCps) / 2;
  const estSec = charCount / avgCps + pauseTotal * 0.8;
  return Math.max(1, Math.round(estSec));
};

const formatDurationLabel = (sec) => {
  const minLabel = i18n.t("Minutes") || "dk";
  const secLabel = i18n.t("Seconds") || "sn";
  if (!sec) return `0 ${secLabel}`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} ${minLabel} ${s} ${secLabel}` : `${s} ${secLabel}`;
};

const AIVoiceTab = forwardRef(
  (
    {
      navItemSelected,
      navItemsIds,
      sceneName,
      voiceProfile,
      onServerModal,
      onCancel,
      onAddSound,
      onEditSound,
      soundList,
      allSoundLists,
      initialSoundIndex,
      initialStartTime,
      initialNarrationText,
      initialNarrationTextNavId,
      onStateChange,
      onInsufficientBalance,
      onCreditCheckReady,
      narrateAllScenes,
    },
    ref
  ) => {
    // The prefill text (Merlin "Anlatım Sesi" ← video transcript) applies ONLY to
    // the scene it was opened for. When the user switches scenes inside the modal,
    // `navItemSelected` changes and this no longer matches → other scenes never
    // inherit this scene's transcript. Empty string = no prefill for this scene.
    const narrationPrefill =
      initialSoundIndex == null &&
      initialNarrationText &&
      (initialNarrationTextNavId == null || initialNarrationTextNavId === navItemSelected)
        ? initialNarrationText
        : "";

    // Detects a credit refusal (org balance or team budget) in TTS API errors. Returns true if handled
    // so the caller can short-circuit the generic error toast. Closing of the
    // parent modal is done upstream (AddSoundModal forwards to EditorModals).
    const checkInsufficientBalance = useCallback(
      (errData) => {
        if (!isCreditRefusal(errData)) return false;
        if (typeof onInsufficientBalance === "function") {
          onInsufficientBalance(errData);
        }
        return true;
      },
      [onInsufficientBalance]
    );
    const sceneIndex = navItemsIds ? navItemsIds.indexOf(navItemSelected) + 1 : 1;
    const totalScenes = navItemsIds ? navItemsIds.length : 1;

    const profileVoiceName = voiceProfile?.voiceDetail?.name || voiceProfile?.voiceName || "";
    const profileService =
      SERVICE_MAP[voiceProfile?.voiceServiceProvider] || voiceProfile?.voiceServiceProvider || "";
    const profileLanguage = voiceProfile?.voiceLanguage || "";
    // Course oluştururken seçilen ElevenLabs modeli (v2_5 | v3). voice-profile
    // endpoint'inden gelir; provider chip'inin ("ElevenLabs 2.5"/"3") doğru
    // seçili gelmesi için kullanılır. Boşsa default (v3).
    const profileElevenLabsModel = voiceProfile?.elevenLabsModel || "";

    const AI_DEFAULT_SERVICE = "elevenlabs";
    const AI_DEFAULT_LANGUAGE = "tr-TR";
    const AI_DEFAULT_VOICE_NAME = "Dağhan Aslanger";

    const normalizeLanguageCode = (lang) => {
      if (!lang) return AI_DEFAULT_LANGUAGE;
      const l = lang.toLowerCase();

      if (l.startsWith("tr")) return "tr-TR";

      if (l === "en-gb" || l === "en-uk") return "en-GB";
      if (l.startsWith("en")) return "en-US";

      if (l.startsWith("es")) return "es-ES";
      if (l.startsWith("de")) return "de-DE";
      if (l.startsWith("fr")) return "fr-FR";
      if (l.startsWith("ja")) return "ja-JP";
      if (l.startsWith("zh")) return "zh-CN";
      if (l.startsWith("az")) return "az-AZ";
      if (l.startsWith("pt")) return "pt-BR";
      if (l.startsWith("ar")) return "ar-SA";

      return lang;
    };

    const defaultSrv = profileService || AI_DEFAULT_SERVICE;
    // Google için dil filtresi uygulanmaz — sayfaya girildiğinde de tüm sesleri
    // getir, kayıtlı/profil diline göre filtreleme yapma.
    const defaultLang =
      defaultSrv === "elevenlabs"
        ? "multilingual"
        : defaultSrv === "google"
          ? ""
          : normalizeLanguageCode(profileLanguage);
    const defaultVoiceName = profileVoiceName || AI_DEFAULT_VOICE_NAME;
    // Provider chip default'u: course'ta seçilen model (v2_5/v3), yoksa v3.
    const defaultElevenLabsModel = profileElevenLabsModel || DEFAULT_ELEVENLABS_MODEL;

    // Sahnede bekleyen taslak (bu sahne için "Ses Oluştur"a basılmış, henüz
    // kaydedilmemiş üretim). Sahne değişince bu bileşen remount olduğu için
    // state narrationDraftStore'da tutulur; mount'ta oradan geri yüklenir.
    // Taslağın sesi zaten soundList'e girmişse (arka plan takipçisi eklemiş)
    // taslak yok sayılır; store'dan silme işi mount effect'inde.
    const mountDraftRef = useRef(undefined);
    if (mountDraftRef.current === undefined) {
      const d = getNarrationDraft(navItemSelected);
      const alreadySaved =
        d?.audioUrl &&
        Array.isArray(soundList) &&
        soundList.some((s) => s?.file && fileBaseName(s.file) === fileBaseName(d.audioUrl));
      mountDraftRef.current = d && !alreadySaved ? d : null;
    }
    const mountDraft = mountDraftRef.current;
    // Taslağın ait olduğu soundList girişi: index kaymış olabilir, dosyadan bul.
    const resolveDraftSoundIndex = (d) => {
      if (!d || d.soundIndex == null || !Array.isArray(soundList)) return null;
      if (soundList[d.soundIndex]?.file === d.soundFile) return d.soundIndex;
      const byFile = d.soundFile ? soundList.findIndex((s) => s?.file === d.soundFile) : -1;
      return byFile >= 0 ? byFile : null;
    };

    const [profileOpen, setProfileOpen] = useState(false);
    const [soundSettingsOpen, setSoundSettingsOpen] = useState(false);
    // null = "create new sound", number = existing soundList[index]
    const [selectedSoundIndex, setSelectedSoundIndex] = useState(() => {
      if (mountDraft) return resolveDraftSoundIndex(mountDraft);
      if (initialSoundIndex === "__new__") return null;
      if (typeof initialSoundIndex === "number") return initialSoundIndex;
      return null;
    });
    const [volume, setVolume] = useState(56);
    const [selectedVoice, setSelectedVoice] = useState(() => mountDraft?.voice || null); // { id, name } objesi - AvatarNarrationTab ile aynı pattern
    const [selectedService, setSelectedService] = useState(() =>
      mountDraft ? mountDraft.service || "" : defaultSrv
    );
    const [selectedGoogleModel, setSelectedGoogleModel] = useState(
      () => mountDraft?.googleModel || DEFAULT_GOOGLE_MODEL
    );
    const [selectedLanguage, setSelectedLanguage] = useState(() =>
      mountDraft ? mountDraft.language || "" : defaultLang
    );
    // ElevenLabs model seçimi ('v2_5' | 'v3'). Course'ta seçilen model (voice-
    // profile) ile başlar → provider chip'i doğru gelir. "ElevenLabs 2.5"/"3"
    // entry'lerinden değişir; üretimde requestBody.elevenLabsModel olarak gider.
    const [selectedElevenLabsModel, setSelectedElevenLabsModel] = useState(
      () => mountDraft?.elevenLabsModel || defaultElevenLabsModel
    );
    const [showGeminiGuide, setShowGeminiGuide] = useState(false);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [geminiStyle, setGeminiStyle] = useState("");
    const [geminiPace, setGeminiPace] = useState("");
    const [geminiAudioProfile, setGeminiAudioProfile] = useState("");
    const [voices, setVoices] = useState([]);
    const [isLoadingVoices, setIsLoadingVoices] = useState(false);
    // Seed the narration text when opened with a pre-filled value (e.g. a video
    // transcript from the Merlin "Anlatım Sesi" flow). Only for a NEW sound —
    // editing an existing one loads its own narrationText.
    const [narrationText, setNarrationText] = useState(() =>
      mountDraft ? mountDraft.text || "" : narrationPrefill
    );
    const [sceneStartTime, setSceneStartTime] = useState(() => {
      if (selectedSoundIndex != null) return 0;
      return initialStartTime > 0 ? initialStartTime : (getEditorPlayhead()?.frame || 0) / 30;
    });
    const mountDraftReady = mountDraft?.status === "ready" && !!mountDraft.audioUrl;
    const [sceneEndTime, setSceneEndTime] = useState(() =>
      mountDraftReady ? mountDraft.duration || 0 : 0
    );
    const [audioUrl, setAudioUrl] = useState(() => (mountDraftReady ? mountDraft.audioUrl : null));
    const [isGenerating, setIsGenerating] = useState(() => mountDraft?.status === "generating");
    const [audioGeneratedInSession, setAudioGeneratedInSession] = useState(
      () => mountDraftReady && !mountDraft.edited
    );
    const [narratorMode, setNarratorMode] = useState(() => mountDraft?.narratorMode || "ai"); // "ai" | "own"
    // Bu sahnenin canlı taslağı (store'a abone) + "Orijinali göster" görünümü.
    const narrationDraft = useNarrationDraft(navItemSelected);
    const [showOriginalText, setShowOriginalText] = useState(false);
    // Kullanıcı ilk düzenlemeyi yaptığında metnin o anki hâli ("orijinal").
    // null = henüz düzenlenmedi. Taslak üretilirken originalText buradan alınır.
    const baselineTextRef = useRef(mountDraft ? (mountDraft.originalText ?? null) : null);
    // Bu instance, başka bir (unmount olmuş) instance'ın başlattığı üretimi
    // bekliyorsa true: sonuç store'a düşünce buradan uygulanır.
    const awaitingDraftRef = useRef(mountDraft?.status === "generating");
    const [showSSML, setShowSSML] = useState(false); // Toggle for SSML view
    const [userTypedText, setUserTypedText] = useState(() => !!narrationPrefill || !!mountDraft); // Tahmini süre sadece kullanıcı yazmaya başlayınca gösterilsin (prefill de sayılır)
    // PPTX speaker note for this scene (slide-notes row), offered as narration text.
    const [slideNote, setSlideNote] = useState(null);
    useEffect(() => {
      let alive = true;
      loadSlideNotes(window.idealStudioTrainingId).then((m) => {
        if (alive) setSlideNote(noteForScene(m, navItemSelected));
      });
      return () => {
        alive = false;
      };
    }, [navItemSelected]);
    const [currentPage, setCurrentPage] = useState(1); // Voice list pagination
    const [validationError, setValidationError] = useState(""); // UI içi doğrulama hatası
    const [validAIVoiceIndices, setValidAIVoiceIndices] = useState([]);
    // Timeline'dan silinmiş ama server'da duran önceki sahne anlatımları.
    // soundList (timeline klibi) ile scene-audios (kalıcı anlatım kaydı) AYRI
    // katmanlar: klip silinince kayıt kalır. Bunlar "geri yükle" seçeneği olur.
    const [restoreCandidates, setRestoreCandidates] = useState([]);
    const [activeRestoreIdx, setActiveRestoreIdx] = useState(null);
    // Geri yüklenen sesin ORİJİNAL adı (server dosya adı). handleAddSound sabit
    // "Scene-N-narration.mp3" üretiyor; restore'da bunun yerine eski ad kullanılır.
    const [restoredSoundName, setRestoredSoundName] = useState("");
    // Custom sahne-sesi dropdown'ının açık/kapalı durumu + dış tıklama kapatma.
    const [selectorOpen, setSelectorOpen] = useState(false);
    const selectorRef = useRef(null);
    // filterVoices bitti mi? İlk seçim (kayıtlı ses vs default anlatıcı) bu
    // bayrak true olunca yapılır; aksi halde init default'u erken basıp flicker olur.
    const [voiceFilterReady, setVoiceFilterReady] = useState(false);
    const checkedSoundsRef = useRef({});
    // Taslak geri yüklendiyse otomatik seçim (loadSavedSound) taslağı ezmesin.
    const hasAutoSelectedFromValid = useRef(!!mountDraft);

    // "Tüm sahneleri seslendir" kutusu işaretliyken kaydete basılınca çıkan onay
    // popup'ı ("emin misiniz?"). pendingSoundData kaydedilecek veriyi tutar.
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);
    const [isNarratingAll, setIsNarratingAll] = useState(false);
    const pendingSoundDataRef = useRef(null);

    // "Tüm sahneler" yalnızca sahnenin ASIL (ilk) anlatımı için anlamlıdır, tek
    // sahnede gereksiz. AI provider sesinde provider BATCH listesinde olmalı; klon
    // (own) sesinde ise seçili bir klon voiceId yeterli — klon ElevenLabs tabanlı
    // olduğundan batch'e provider "elevenlabs" + voiceId ile gider (AI ile aynı mantık).
    // Ayrıca kullanıcı bu sahneyi BU OTURUMDA seslendirmeden kutu gösterilmez
    // (audioGeneratedInSession) — yoksa daha hiç ses üretmeden "tüm sahneler"
    // önerisi kafa karıştırıyor. Kutu (footer'daki checkbox) bu bayrak true olunca çıkar.
    const isPrimarySound =
      selectedSoundIndex === 0 ||
      (selectedSoundIndex == null && (!soundList || soundList.length === 0));
    const eligibleForAllScenes =
      isPrimarySound &&
      totalScenes > 1 &&
      audioGeneratedInSession &&
      (narratorMode === "own"
        ? !!selectedVoice?.id
        : BATCH_NARRATION_PROVIDERS.includes(selectedService));

    useEffect(() => {
      let active = true;

      const filterVoices = async () => {
        if (!soundList || soundList.length === 0) {
          if (active) {
            setValidAIVoiceIndices([]);
            setVoiceFilterReady(true);
          }
          return;
        }

        // getSceneAudios (network ~1sn) yalnızca gerçekten gerekince çağrılır:
        // voiceData/narrationText/metadata'sı olan girişler için gerekmez. Hepsi
        // doluysa filter anında biter → kart "-" beklemeden kayıtlı sesi gösterir.
        let sceneAudios = null;
        const getSceneAudiosCached = async () => {
          if (sceneAudios !== null) return sceneAudios;
          sceneAudios = [];
          try {
            if (navItemSelected) {
              const data = await SceneAudioService.getSceneAudios();
              const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
              const scene = scenes.find((s) => s.navId === navItemSelected);
              if (scene?.audios) sceneAudios = scene.audios;
            }
          } catch (error) {
            console.error("Error fetching scene audios for filtering", error);
          }
          return sceneAudios;
        };

        const validIndices = [];
        for (let i = 0; i < soundList.length; i++) {
          const target = soundList[i];
          if (!target) continue;

          if (target.file && checkedSoundsRef.current[target.file] !== undefined) {
            if (checkedSoundsRef.current[target.file]) validIndices.push(i);
            continue;
          }

          let isValid = false;
          if (target.voiceData || target.narrationText) {
            isValid = true;
          } else if (
            target.metadata &&
            (target.metadata.VoiceId || target.metadata.VoiceName || target.metadata.VoiceText)
          ) {
            isValid = true;
          } else if (target.file) {
            const targetFileName = target.file.split("/").pop().split("?")[0];
            const audios = await getSceneAudiosCached();
            const matchedAudio = audios.find((a) => {
              if (!a.filePath) return false;
              return a.filePath.split("/").pop().split("?")[0] === targetFileName;
            });

            if (matchedAudio?.narrationTextOriginal) {
              isValid = true;
            } else {
              try {
                const metaUrl = withSidecarSuffix(target.file, ".txt");
                const response = await fetch(metaUrl, { method: "HEAD", credentials: "include" });
                if (response.ok) {
                  isValid = true;
                }
              } catch (e) {
                console.error("Error fetching meta url", e);
              }
            }
          }

          if (target.file) {
            checkedSoundsRef.current[target.file] = isValid;
          }
          if (isValid) {
            validIndices.push(i);
          }
        }

        if (active) {
          setValidAIVoiceIndices(validIndices);
          setVoiceFilterReady(true);
        }
      };

      filterVoices();

      return () => {
        active = false;
      };
    }, [soundList, navItemSelected]);

    // Server'daki scene-audios kaydını çek; soundList'te (timeline) karşılığı
    // OLMAYAN, metni bulunan anlatımları "geri yükle" adayı olarak sun. Böylece
    // klip timeline'dan silinse de orijinal sahne sesi geri getirilebilir.
    useEffect(() => {
      let active = true;
      const baseName = (p) => (p ? p.split("/").pop().split("?")[0] : "");
      const loadRestoreCandidates = async () => {
        if (!navItemSelected) {
          if (active) setRestoreCandidates([]);
          return;
        }
        try {
          const existing = new Set(
            (Array.isArray(soundList) ? soundList : [])
              .map((s) => baseName(s?.file))
              .filter(Boolean)
          );
          const data = await SceneAudioService.getSceneAudios();
          const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
          const scene = scenes.find((s) => s.navId === navItemSelected);
          const audios = Array.isArray(scene?.audios) ? scene.audios : [];
          const candidates = audios
            .filter(
              (a) => a?.filePath && a?.narrationTextOriginal && !existing.has(baseName(a.filePath))
            )
            // Sahne başlığını taşı: navItems boşsa scene.sceneTitle kullanılır
            // (NarrationTextsPanel ile aynı kaynak). İsim = "Narration - <başlık>".
            .map((a) => ({ ...a, sceneTitle: scene?.sceneTitle || "" }));
          if (active) setRestoreCandidates(candidates);
        } catch (error) {
          console.error("Error loading restore candidates", error);
          if (active) setRestoreCandidates([]);
        }
      };
      loadRestoreCandidates();
      return () => {
        active = false;
      };
    }, [soundList, navItemSelected]);

    // İlk ses seçimi — SADECE filterVoices bitince (voiceFilterReady) çalışır.
    // selectedSoundIndex zaten bir sayıysa (mevcut ses tıklanmış) init halleder.
    // Kayıtlı geçerli AI sesi varsa onu seç+yükle; yoksa (sadece yüklenmiş ses
    // vb.) default anlatıcıyı seç. Default'u init'te ERKEN basmadığımız için
    // "önce profil sesi → ~1sn sonra kayıtlı ses" flicker'ı oluşmaz.
    useEffect(() => {
      if (hasAutoSelectedFromValid.current) return;
      if (selectedSoundIndex !== null) return;
      if (!voiceFilterReady) return;

      if (validAIVoiceIndices.length > 0) {
        hasAutoSelectedFromValid.current = true;
        const firstIdx = validAIVoiceIndices[0];
        setSelectedSoundIndex(firstIdx);
        setAudioGeneratedInSession(false);
        loadedSoundUrlRef.current = null;
        if (soundList && soundList[firstIdx]) {
          // Load the existing scene sound so its voice/provider come preselected
          // (the user can still change them). When opened with a prefill (Merlin
          // "Anlatım Sesi" ← video transcript), feed the transcript INTO
          // loadSavedSound as the sound's text: every internal setNarrationText
          // step then uses the transcript (and loadNarrationText skips its own
          // fetch because narrationText is already set), so the text shows
          // instantly from the seed and never flickers to the old/empty value
          // while the ~1s load runs. The .finally is a final safety net.
          const hasPrefill = !!narrationPrefill;
          const soundForLoad = hasPrefill
            ? { ...soundList[firstIdx], narrationText: narrationPrefill }
            : soundList[firstIdx];
          Promise.resolve(loadSavedSound(soundForLoad)).finally(() => {
            if (hasPrefill) setNarrationText(narrationPrefill);
          });
        }
      } else if (Array.isArray(soundList) && soundList.length > 0) {
        // soundList'te giriş var ama geçerli AI sesi yok → init default seçimini
        // ertelemişti; şimdi default anlatıcıyı seç.
        hasAutoSelectedFromValid.current = true;
        loadVoices(defaultSrv, defaultLang, "", 1, defaultVoiceName);
        // Profil sesi (kurs sesi) page 1'de olmayabilir → autoSelect isimle
        // bulamaz. ID ile TÜM sayfalardan çöz ve kartı set et.
        if (voiceProfile?.voiceName) {
          resolveVoiceAcrossPages(defaultSrv, defaultLang, "", voiceProfile.voiceName).then((v) => {
            if (v) setSelectedVoice(v);
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validAIVoiceIndices, voiceFilterReady]);

    useEffect(() => {
      onStateChange?.({
        isGenerating,
        audioGeneratedInSession,
        // Yeni ses üretilmese de kaydedilebilir: geri yüklenmiş önceki sahne
        // sesi (activeRestoreIdx) için audioUrl hazır → Kaydet açık olsun.
        readyToSave: audioGeneratedInSession || (activeRestoreIdx != null && !!audioUrl),
        canNarrateAllScenes: eligibleForAllScenes,
      });
    }, [
      isGenerating,
      audioGeneratedInSession,
      activeRestoreIdx,
      audioUrl,
      eligibleForAllScenes,
      onStateChange,
    ]);

    const [selectedGender, setSelectedGender] = useState(""); // Gender filter
    const [totalPages, setTotalPages] = useState(1); // Total pages from API

    const audioRef = useRef(null);
    const sampleAudioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(() =>
      mountDraftReady ? mountDraft.duration || 0 : 0
    );
    const [progress, setProgress] = useState(0);
    // Taslak geri yüklendiyse soundList effect'i kayıtlı sesi yeniden yükleyip
    // taslağı ezmesin diye ilgili girişin dosyası "yüklendi" sayılır.
    const loadedSoundUrlRef = useRef(
      mountDraft && selectedSoundIndex != null
        ? soundList?.[selectedSoundIndex]?.file || null
        : null
    );
    // Bu oturumda üretilen TTS job'u + sesi. Ses sahneye kaydedilince job approve
    // edilir; aksi halde widget'ta "Onay Bekliyor" kalır (AIImageGenerateTab ile aynı).
    const generatedTtsJobRef = useRef(null);
    const [samplePlayingId, setSamplePlayingId] = useState(null);
    const [sampleLanguage, setSampleLanguage] = useState(""); // Global sample language for all voices

    // Voice cloning states
    const [clonedVoices, setClonedVoices] = useState([]);
    const [isLoadingClonedVoices, setIsLoadingClonedVoices] = useState(false);
    // Kart üzerindeki çöp kutusu → onay penceresi; busy iken çift silme engellenir.
    const [voiceToDelete, setVoiceToDelete] = useState(null);
    const [isDeletingVoice, setIsDeletingVoice] = useState(false);
    const [isUploadingVoice, setIsUploadingVoice] = useState(false);
    const [cloneVoiceName, setCloneVoiceName] = useState("");
    const [cloneVoiceGender, setCloneVoiceGender] = useState("");
    const [cloneVoiceFile, setCloneVoiceFile] = useState(null);
    const [cloneVoiceFilePreviewUrl, setCloneVoiceFilePreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    // Microphone recording states
    const [uploadMode, setUploadMode] = useState("file"); // "file" | "microphone"
    const [isRecording, setIsRecording] = useState(false);
    const [randomRecordingTextIndex, setRandomRecordingTextIndex] = useState(null);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordingBlob, setRecordingBlob] = useState(null);
    const [recordingAudioUrl, setRecordingAudioUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const recordingTexts = [
      i18n.t("RecordingText1"),
      i18n.t("RecordingText2"),
      i18n.t("RecordingText3"),
      i18n.t("RecordingText4"),
      i18n.t("RecordingText5"),
      i18n.t("RecordingText6"),
      i18n.t("RecordingText7"),
      i18n.t("RecordingText8"),
      i18n.t("RecordingText9"),
      i18n.t("RecordingText10"),
    ];

    // Select random text when switching to microphone mode
    useEffect(() => {
      if (uploadMode === "microphone" && randomRecordingTextIndex === null) {
        const randomIndex = Math.floor(Math.random() * recordingTexts.length);
        setRandomRecordingTextIndex(randomIndex);
      }
    }, [uploadMode, randomRecordingTextIndex, recordingTexts.length]);

    // Microphone Recording Functions
    const startMicrophoneRecording = useCallback(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Try to use audio/wav if supported, fallback to webm with opus
        let mimeType = "audio/webm;codecs=opus";
        if (MediaRecorder.isTypeSupported("audio/wav")) {
          mimeType = "audio/wav";
        } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
          mimeType = "audio/webm;codecs=opus";
        }

        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onstop = () => {
          // Create blob with appropriate type
          const blob = new Blob(chunks, { type: mimeType });
          setRecordingBlob(blob);
          const audioUrl = URL.createObjectURL(blob);
          setRecordingAudioUrl(audioUrl);
          stream.getTracks().forEach((track) => track.stop());
        };

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
        setRecordedChunks(chunks);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        onServerModal?.(
          i18n.t("error.warning"),
          `<span>${i18n.t("MicrophonePermissionDenied")}</span>`,
          false
        );
      }
    }, [onServerModal]);

    const stopMicrophoneRecording = useCallback(() => {
      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
        setIsRecording(false);
        setMediaRecorder(null);
      }
    }, [mediaRecorder, isRecording]);

    const resetRecording = useCallback(() => {
      if (recordingAudioUrl) {
        URL.revokeObjectURL(recordingAudioUrl);
        setRecordingAudioUrl(null);
      }
      setRandomRecordingTextIndex(null);
      setRecordingBlob(null);
      setRecordedChunks([]);
      setIsRecording(false);
      if (mediaRecorder) {
        mediaRecorder.stop();
        setMediaRecorder(null);
      }
    }, [mediaRecorder, recordingAudioUrl]);

    // File handling functions
    const handleFileSelect = useCallback(
      (file) => {
        if (!file) return;

        // 500 MB üst sınır guard'ı — aşan dosyayı kabul etme.
        if (isFileTooLarge(file)) {
          onServerModal?.(i18n.t("error.warning"), `<span>${fileTooLargeMessage()}</span>`, false);
          return;
        }

        // Check if file is an audio file
        const validAudioTypes = [
          "audio/mpeg",
          "audio/mp3",
          "audio/wav",
          "audio/wave",
          "audio/x-wav",
          "audio/ogg",
          "audio/webm",
          "audio/aac",
          "audio/m4a",
          "audio/x-m4a",
          "audio/flac",
          "audio/mp4",
          "audio/x-ms-wma",
        ];

        if (
          !validAudioTypes.includes(file.type) &&
          !file.name.match(/\.(mp3|wav|ogg|webm|m4a|aac|flac|wma)$/i)
        ) {
          onServerModal?.(
            i18n.t("error.warning"),
            `<span>${i18n.t("PleaseSelectAudioFile") || "Lütfen geçerli bir ses dosyası seçin"}</span>`,
            false
          );
          return;
        }

        // Cleanup previous preview URL
        if (cloneVoiceFilePreviewUrl) {
          URL.revokeObjectURL(cloneVoiceFilePreviewUrl);
        }

        // Create preview URL for the file
        const previewUrl = URL.createObjectURL(file);
        setCloneVoiceFile(file);
        setCloneVoiceFilePreviewUrl(previewUrl);
      },
      [onServerModal, cloneVoiceFilePreviewUrl]
    );

    const handleDragEnter = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDragOver = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
          handleFileSelect(files[0]);
        }
      },
      [handleFileSelect]
    );

    // Voice Cloning API Functions
    const cloneVoice = useCallback(
      async (file, name, gender) => {
        const organizationId = getOrganizationId();
        if (!organizationId) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.organizationIdNotFound") ||
                "Organization ID not found in token. Please login again.") +
              "</span>",
            false
          );
          return null;
        }

        setIsUploadingVoice(true);
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("name", name);
          formData.append("gender", gender);
          formData.append("organizationId", organizationId);
          const durationSeconds = await getMediaDuration(file);
          if (durationSeconds != null) formData.append("durationSeconds", String(durationSeconds));

          const response = await fetch(`${process.env.MW_API_URL}api/tts-avatar/voices/clone`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();
            onServerModal?.(
              i18n.t("Success") || "Başarılı",
              `<span>${i18n.t("VoiceClonedSuccessfully") || "Ses başarıyla klonlandı!"}</span>`,
              false
            );
            return data;
          } else {
            const errorData = await response.json().catch(() => ({}));
            if (checkInsufficientBalance(errorData)) {
              return null;
            }
            onServerModal?.(
              i18n.t("error.warning"),
              `<span>${
                errorData.message || i18n.t("error.failedToCloneVoice") || "Ses klonlanamadı"
              }</span>`,
              false
            );
            return null;
          }
        } catch (error) {
          console.error("Error cloning voice:", error);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.networkError") || "İğ hatası. Lütfen tekrar deneyin.") +
              "</span>",
            false
          );
          return null;
        } finally {
          setIsUploadingVoice(false);
        }
      },
      [onServerModal, checkInsufficientBalance]
    );

    const loadClonedVoices = useCallback(async () => {
      const organizationId = getOrganizationId();
      if (!organizationId) {
        console.warn("Organization ID not found in token");
        return [];
      }

      setIsLoadingClonedVoices(true);
      try {
        const params = new URLSearchParams();
        params.append("organizationId", organizationId);
        params.append("providerName", "private");

        const response = await fetch(
          `${process.env.MW_API_URL}api/tts-avatar/voices?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const voices = Array.isArray(data.voices) ? data.voices : data;
          setClonedVoices(voices);
          return voices;
        } else {
          console.error("Failed to load cloned voices");
          return [];
        }
      } catch (error) {
        console.error("Error loading cloned voices:", error);
        return [];
      } finally {
        setIsLoadingClonedVoices(false);
      }
    }, []);

    const deleteClonedVoice = useCallback(
      async (voiceId) => {
        const organizationId = getOrganizationId();
        if (!organizationId) {
          console.warn("Organization ID not found in token");
          return false;
        }

        try {
          // Kullanıcı route'u: admin/voices/:id SuperAdminGuard arkasında olduğu
          // için normal kullanıcıda 403 dönüyordu. Org artık query ile değil
          // JWT'den okunuyor; sunucu sesin o org'a ait olduğunu doğruluyor.
          const response = await fetch(
            `${process.env.MW_API_URL}api/tts-avatar/voices/${voiceId}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            }
          );

          if (response.ok) {
            onServerModal?.(
              i18n.t("Success") || "Başarılı",
              `<span>${i18n.t("VoiceDeletedSuccessfully") || "Ses başarıyla silindi!"}</span>`,
              false
            );
            return true;
          } else {
            onServerModal?.(
              i18n.t("error.warning"),
              "<span>" + (i18n.t("error.failedToDeleteVoice") || "Ses silinemedi") + "</span>",
              false
            );
            return false;
          }
        } catch (error) {
          console.error("Error deleting voice:", error);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.networkError") || "Ağ hatası. Lütfen tekrar deneyin.") +
              "</span>",
            false
          );
          return false;
        }
      },
      [onServerModal]
    );

    const confirmDeleteClonedVoice = useCallback(async () => {
      if (!voiceToDelete || isDeletingVoice) return;
      const target = voiceToDelete;
      setIsDeletingVoice(true);
      try {
        const ok = await deleteClonedVoice(target.id);
        if (ok) {
          // Silinen ses seçiliyse seçimi bırak; aksi halde "Ses Oluştur" artık
          // var olmayan bir sesle istek atardı.
          setSelectedVoice((prev) => (prev?.id === target.id ? null : prev));
          setClonedVoices((prev) => prev.filter((v) => v.id !== target.id));
          loadClonedVoices();
        }
      } finally {
        setIsDeletingVoice(false);
        setVoiceToDelete(null);
      }
    }, [voiceToDelete, isDeletingVoice, deleteClonedVoice, loadClonedVoices]);

    const generateTTSWithClonedVoice = useCallback(
      async (text, voiceId) => {
        const organizationId = getOrganizationId();
        if (!organizationId) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.organizationIdNotFound") ||
                "Organization ID not found in token. Please login again.") +
              "</span>",
            false
          );
          return null;
        }

        setIsGenerating(true);
        try {
          const response = await fetch(`${process.env.MW_API_URL}api/tts-avatar/tts/generate`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({
              text: text.trim(),
              voiceId: voiceId,
              organizationId: organizationId,
              idealStudioTrainingId: window.idealStudioTrainingId,
              navId: navItemSelected,
              navIndex: String(sceneIndex),
            }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.fileUrl) {
              setAudioUrl(data.fileUrl);
              setAudioGeneratedInSession(true);
              loadedSoundUrlRef.current = null;
              setIsPlaying(false);
              setCurrentTime(0);
              setProgress(0);
              setDuration(data.duration || 0);
              setSceneEndTime(data.duration || 0);
              return data;
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            if (checkInsufficientBalance(errorData)) {
              return null;
            }
            onServerModal?.(
              i18n.t("error.warning"),
              "<span>" +
                (i18n.t("error.failedToGenerateAudio") || "Failed to generate audio") +
                "</span>",
              false
            );
          }
        } catch (error) {
          console.error("Error generating TTS:", error);
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("error.networkError") || "Ağ hatası. Lütfen tekrar deneyin.") +
              "</span>",
            false
          );
        } finally {
          setIsGenerating(false);
        }
        return null;
      },
      [onServerModal, navItemSelected, sceneIndex, checkInsufficientBalance]
    );

    // Provider-specific language options
    const getLanguageOptions = (service) => {
      const serviceToUse = service || selectedService;
      if (!serviceToUse) return [];

      const languagesByProvider = {
        google: [
          { value: "ar-SA", label: i18n.t("LanguageNames.Arabic") || "Arabic" },
          { value: "de-DE", label: i18n.t("LanguageNames.German") || "German" },
          { value: "en-US", label: i18n.t("LanguageNames.English") || "English (US)" },
          { value: "es-ES", label: i18n.t("LanguageNames.Spanish") || "Spanish" },
          { value: "fr-FR", label: i18n.t("LanguageNames.French") || "French" },
          { value: "ja-JP", label: i18n.t("LanguageNames.Japanese") || "Japanese" },
          { value: "tr-TR", label: i18n.t("LanguageNames.Turkish") || "Turkish" },
          { value: "az-AZ", label: i18n.t("LanguageNames.Azerbaijani") || "Azerbaijani" },
        ],
        elevenlabs: [
          { value: "multilingual", label: i18n.t("LanguageNames.Multilingual") || "Multilingual" },
        ],
        azure: [
          { value: "de-DE", label: i18n.t("LanguageNames.German") || "German" },
          { value: "en-GB", label: i18n.t("LanguageNames.EnglishUK") || "English (UK)" },
          { value: "en-US", label: i18n.t("LanguageNames.English") || "English (US)" },
          { value: "es-ES", label: i18n.t("LanguageNames.Spanish") || "Spanish" },
          { value: "fr-FR", label: i18n.t("LanguageNames.French") || "French" },
          { value: "ja-JP", label: i18n.t("LanguageNames.Japanese") || "Japanese" },
          { value: "multilingual", label: i18n.t("LanguageNames.Multilingual") || "Multilingual" },
          { value: "pt-BR", label: i18n.t("LanguageNames.Portuguese") || "Portuguese (BR)" },
          { value: "tr-TR", label: i18n.t("LanguageNames.Turkish") || "Turkish" },
          { value: "az-AZ", label: i18n.t("LanguageNames.Azerbaijani") || "Azerbaijani" },
        ],
        sestek: [
          { value: "tr-TR", label: i18n.t("LanguageNames.Turkish") || "Turkish" },
          { value: "en-US", label: i18n.t("LanguageNames.English") || "English (US)" },
          { value: "de-DE", label: i18n.t("LanguageNames.German") || "German" },
        ],
      };

      return languagesByProvider[serviceToUse] || [];
    };

    // SSML etiketlerini temizleyen fonksiyon
    const stripSSML = (text) => {
      if (!text) return "";
      return text.replace(/<[^>]*>/g, "");
    };

    // Görüntülenecek metin (SSML göster/gizle durumuna göre)
    const displayedText = showSSML ? narrationText : stripSSML(narrationText);

    const loadNarrationText = useCallback(
      async (targetSound) => {
        if (!navItemSelected) return;
        try {
          const data = await SceneAudioService.getSceneAudios();
          const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
          const scene = scenes.find((s) => s.navId === navItemSelected);
          if (!scene?.audios) return;

          let matchedAudio = null;
          if (targetSound?.file) {
            const targetFileName = targetSound.file.split("/").pop().split("?")[0];
            matchedAudio = scene.audios.find((a) => {
              if (!a.filePath) return false;
              const audioFileName = a.filePath.split("/").pop().split("?")[0];
              return audioFileName === targetFileName;
            });
          }
          if (!matchedAudio) {
            matchedAudio = scene.audios.find((a) => a.filePath);
          }

          // Redux narrationText (kullanıcının kaydettiği düzenleme) önceliklidir.
          // DB narrationTextOriginal yalnızca TTS üretiminde güncellendiği için,
          // düzenlenip yeniden üretilmeyen metinlerde DB eski kalır. Bu yüzden DB'ye
          // SADECE Redux narrationText boşken (yeni yüklenen eğitim) düşülür.
          if (matchedAudio?.narrationTextOriginal && !targetSound?.narrationText) {
            setNarrationText(matchedAudio.narrationTextOriginal);
          }
        } catch (error) {
          console.error("Error loading narration text:", error);
        }
      },
      [navItemSelected]
    );

    const loadVoices = useCallback(
      async (service, language, gender = "", page = 1, autoSelectName = "") => {
        setIsLoadingVoices(true);
        const serviceName = API_SERVICE_MAP[service] || service;
        try {
          // Build query parameters
          const params = new URLSearchParams();
          if (serviceName) params.append("service", serviceName);
          if (language) params.append("language", language);
          if (gender) params.append("gender", gender);
          params.append("page", page.toString());
          params.append("pageSize", "12");

          const response = await fetch(
            `${process.env.MW_API_URL}api/course/voices?${params.toString()}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const list = Array.isArray(data?.voices) ? data.voices : [];
              setVoices(list);

              // Set total pages from API response
              if (data.pagination?.totalPages) {
                setTotalPages(data.pagination.totalPages);
              } else if (data.pagination?.total) {
                setTotalPages(Math.ceil(data.pagination.total / 12));
              } else if (data.totalPages) {
                setTotalPages(data.totalPages);
              } else if (data.totalCount) {
                setTotalPages(Math.ceil(data.totalCount / 12));
              } else {
                // If no pagination info, assume there might be more pages if we got 12 items
                setTotalPages(list.length >= 12 ? page + 1 : page);
              }

              if (autoSelectName) {
                const match = list.find(
                  (v) => v.name === autoSelectName || v.id === autoSelectName
                );
                if (match) {
                  setSelectedVoice(match);
                }
              }
              return list;
            }
          }
        } catch (error) {
          console.error("Error loading voices:", error);
        } finally {
          setIsLoadingVoices(false);
        }
        return [];
      },
      []
    );

    // Seçili sesi TÜM sayfalar arasından çözer (grid `voices` state'ini
    // DEĞİŞTİRMEDEN). Grid 12'şer sayfalı kalır; bu yalnız açılışta kartta doğru
    // ismi göstermek için tek seferlik büyük fetch yapar → ses page 2+'da olsa
    // bile baştan görünür. Grid'de o sayfaya gelince zaten highlight olur.
    const resolveVoiceAcrossPages = useCallback(async (service, language, gender, voiceId) => {
      if (!voiceId) return null;
      const serviceName = API_SERVICE_MAP[service] || service;
      try {
        const params = new URLSearchParams();
        if (serviceName) params.append("service", serviceName);
        if (language) params.append("language", language);
        if (gender) params.append("gender", gender);
        params.append("page", "1");
        params.append("pageSize", "200");
        const response = await fetch(
          `${process.env.MW_API_URL}api/course/voices?${params.toString()}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!response.ok) return null;
        const data = await response.json();
        const list = Array.isArray(data?.voices) ? data.voices : [];
        const wanted = String(voiceId).toLowerCase().trim();
        return (
          list.find(
            (v) =>
              String(v.id || "")
                .toLowerCase()
                .trim() === wanted
          ) || null
        );
      } catch (e) {
        console.error("resolveVoiceAcrossPages failed", e);
        return null;
      }
    }, []);

    const loadSavedSound = useCallback(
      async (soundEntry) => {
        const target = soundEntry || (soundList && soundList.length > 0 ? soundList[0] : null);
        if (!target) return;
        // Yeni bir ses yükleniyor → "orijinal metin" referansı sıfırlanır.
        baselineTextRef.current = null;
        setShowOriginalText(false);

        // Eğitim üretiminde seslendirme (TTS) başarısız olan sahneler: ses dosyası
        // üretilemez ama narration metni soundList girişinde (narrationText) saklanır.
        // Dosya yok diye erken dönersek panel boş kalıyordu. Metni yükle ki kullanıcı
        // görsün ve "Yeniden Oluştur" ile tekrar seslendirebilsin (ses oynatıcı pasif).
        if (!target.file) {
          setNarrationText(target.narrationText || "");
          setAudioUrl(null);
          setAudioGeneratedInSession(false);
          if (target.volumeLevel != null) {
            setVolume(Math.round(target.volumeLevel * 100));
          }
          if (target.startTime != null) {
            setSceneStartTime(target.startTime);
          }
          if (target.endTime != null) {
            setSceneEndTime(target.endTime);
            setDuration(Math.max(0, (target.endTime || 0) - (target.startTime || 0)));
          }
          loadedSoundUrlRef.current = null;
          // Yeniden seslendirme için bir anlatıcı seçili gelsin (girişte voiceData yok).
          loadVoices(defaultSrv, defaultLang, "", 1, defaultVoiceName);
          return;
        }

        // Eğer aynı ses dosyası zaten yüklenmişse, tekrar yükleme
        if (loadedSoundUrlRef.current === target.file) {
          return;
        }

        // Audio URL'i hemen güncelle. Aşağıdaki ses eşleştirme (loadVoices) bir
        // ağ çağrısı (~1sn) içerir; src'yi await'lerin sonuna bırakırsak o süre
        // boyunca oynatıcı eski sesi gösterir (flicker). Yeni dosya kesin, erken set et.
        setAudioUrl(target.file);
        setAudioGeneratedInSession(false);

        // voiceData'yı önce kullan (AvatarNarrationTab ile aynı pattern)
        // .txt dosyası sadece voiceData yoksa kullanılır (ezmez!)
        let metadata = null;
        let provider = null;

        if (target.voiceData) {
          metadata = {
            VoiceServiceType:
              target.voiceData.provider ||
              (target.voiceData.narratorMode === "own" ? "private" : ""),
            VoiceName: target.voiceData.voiceName || "",
            VoiceId: target.voiceData.voiceId || "",
            VoiceText: target.narrationText || "",
            VoiceLanguage: target.voiceData.language || "",
            VoiceGender: target.voiceData.gender || "",
            NarratorMode: target.voiceData.narratorMode || "ai",
          };
        }

        // voiceData yoksa .txt'yi dene (AvatarNarrationTab ile aynı)
        if (!metadata) {
          try {
            const metaUrl = withSidecarSuffix(target.file, ".txt");
            const response = await fetch(metaUrl, { credentials: "include" });
            if (response.ok) {
              const text = await response.text();
              if (text.indexOf("{") === 0) {
                metadata = JSON.parse(text);
              }
            }
          } catch (error) {
            // Metadata dosyası yoksa devam et
          }
        }

        // Metadata varsa işle
        if (metadata && (metadata.VoiceId || metadata.VoiceName) && metadata.VoiceText) {
          // Ses metnini yükle
          setNarrationText(metadata.VoiceText);

          // Provider normalization
          provider = metadata.VoiceServiceType;
          if (provider === "Microsoft" || provider === 0) {
            provider = "azure";
          } else if (provider === 1) {
            provider = "elevenlabs";
          } else if (provider === 2 || provider === "gemini") {
            provider = "google";
          } else if (provider === "private" || provider === "cloned") {
            provider = "private";
          }

          // Own voice (cloned voice) durumunu kontrol et
          if (provider === "private" || metadata.NarratorMode === "own") {
            setNarratorMode("own");
            setSelectedService("");
            setSelectedLanguage("");
            setSelectedGender("");
            setSelectedElevenLabsModel(DEFAULT_ELEVENLABS_MODEL);

            const voiceIdToMatch = metadata.VoiceId || metadata.VoiceName;

            // Kartı hemen göster; loadClonedVoices ağ çağrısı boyunca (~1sn)
            // eski ses kalmasın (flicker). matchedVoice aşağıda ezer. Ad id ise
            // "" ver — bu ~1sn'de bile ham id görünmesin ("-" gösterilir).
            setSelectedVoice({
              id: voiceIdToMatch,
              name: realVoiceName(metadata.VoiceName, voiceIdToMatch),
            });

            const clonedVoicesList = await loadClonedVoices();

            const matchedVoice = clonedVoicesList?.find((v) => {
              const vId = String(v.id || "")
                .toLowerCase()
                .trim();
              const vName = String(v.name || "")
                .toLowerCase()
                .trim();
              const searchId = String(voiceIdToMatch || "")
                .toLowerCase()
                .trim();
              const searchName = String(metadata.VoiceName || "")
                .toLowerCase()
                .trim();
              return (
                vId === searchId || vName === searchName || vId === searchName || vName === searchId
              );
            });

            if (matchedVoice) {
              // İsim tek kaynaktan gelsin: kayıtlı voiceData.voiceName (exportedState).
              // matchedVoice klon ses verisini (id vb.) korur, sadece görünen isim
              // kayıtlı değerle sabitlenir; aksi halde erken set ~1sn sonra ezilip değişir.
              setSelectedVoice({
                ...matchedVoice,
                name:
                  realVoiceName(
                    target.voiceData?.voiceName || metadata.VoiceName,
                    voiceIdToMatch
                  ) || matchedVoice.name,
              });
            } else {
              // Eşleşme yok: gerçek ad varsa onu, hiç ad yoksa son çare id göster.
              setSelectedVoice({
                id: voiceIdToMatch,
                name: realVoiceName(metadata.VoiceName, voiceIdToMatch) || voiceIdToMatch,
              });
            }
          } else {
            // AI voice mode
            setNarratorMode("ai");
            setSelectedService(provider);

            if (provider === "google") {
              setSelectedGoogleModel(DEFAULT_GOOGLE_MODEL);
              const savedStyle = target.voiceData?.style;
              const savedPace = target.voiceData?.pace;
              const savedProfile = target.voiceData?.audioProfile;
              setGeminiStyle(
                GEMINI_TTS_STYLE_OPTIONS.some((o) => o.value === savedStyle) ? savedStyle : ""
              );
              setGeminiPace(
                GEMINI_TTS_PACE_OPTIONS.some((o) => o.value === savedPace) ? savedPace : ""
              );
              setGeminiAudioProfile(typeof savedProfile === "string" ? savedProfile : "");
            } else {
              setGeminiStyle("");
              setGeminiPace("");
              setGeminiAudioProfile("");
            }

            let lang = metadata.VoiceLanguage ? normalizeLanguageCode(metadata.VoiceLanguage) : "";
            if (provider === "elevenlabs") {
              lang = "multilingual";
            } else if (provider === "google") {
              // Google için dil filtresi uygulanmaz — sayfaya girildiğinde de
              // kayıtlı dile göre filtreleme yapma, tüm sesleri yükle.
              lang = "";
            }

            setSelectedLanguage(lang);
            // ElevenLabs model önceliği: kayıtlı sesin voiceData'sı → dosya
            // metadata'sı (.txt sidecar'daki ElevenLabsModel) → course profili
            // (voice-profile) → default (v3).
            setSelectedElevenLabsModel(
              provider === "elevenlabs"
                ? target.voiceData?.elevenLabsModel ||
                    metadata.ElevenLabsModel ||
                    profileElevenLabsModel ||
                    DEFAULT_ELEVENLABS_MODEL
                : DEFAULT_ELEVENLABS_MODEL
            );
            if (metadata.VoiceGender) setSelectedGender(metadata.VoiceGender);

            const gend = metadata.VoiceGender || "";
            const voiceIdToMatch = metadata.VoiceId || metadata.VoiceName;

            // Ses kartını (avatar + isim) hemen metadata'dan göster. loadVoices bir
            // ağ çağrısı (~1sn); eşleşen sesi await sonrası beklersek kart o süre
            // boyunca eski sesi gösterir (flicker). Aşağıdaki matchedVoice bunu
            // daha zengin veriyle (gender vb.) ezer.
            setSelectedVoice({
              id: voiceIdToMatch,
              name: realVoiceName(
                target.voiceData?.voiceName || metadata.VoiceName,
                voiceIdToMatch
              ),
              gender: gend,
            });

            // autoSelect verme: loadVoices içindeki setSelectedVoice(match) ham API
            // objesini (API adıyla) araya basıp flicker yaratıyordu. Seçimi aşağıdaki
            // matchedVoice bloğu tek isim kaynağıyla (voiceData.voiceName) yapar.
            const list = await loadVoices(provider, lang, gend, 1, "");

            const searchId = String(voiceIdToMatch || "")
              .toLowerCase()
              .trim();
            const searchName = String(metadata.VoiceName || "")
              .toLowerCase()
              .trim();

            const cleanLegacyVoiceName = (name) => {
              if (!name) return "";
              if (provider === "azure" || provider === "microsoft") {
                let clean = name.split(":")[0];
                if (clean.includes("-")) {
                  const parts = clean.split("-");
                  clean = parts[parts.length - 1];
                }
                clean = clean.replace(/Neural$/i, "");
                return clean;
              }
              return name;
            };

            const cleanSearchName = cleanLegacyVoiceName(metadata.VoiceName || voiceIdToMatch)
              .toLowerCase()
              .trim();

            const matchedVoice = list.find((v) => {
              const vId = String(v.id || "")
                .toLowerCase()
                .trim();
              const vName = String(v.name || "")
                .toLowerCase()
                .trim();
              return (
                vId === searchId ||
                vName === searchName ||
                vId === searchName ||
                vName === searchId ||
                (cleanSearchName && vName === cleanSearchName) ||
                (vName.length > 2 && searchId.includes(vName)) ||
                (vName.length > 2 && searchName.includes(vName))
              );
            });

            if (matchedVoice) {
              // İsim tek kaynaktan gelsin: kayıtlı voiceData.voiceName (exportedState).
              // matchedVoice'in id/gender/sampleUrls alanları korunur (grid seçimi +
              // örnek çalma için gerekli), sadece görünen isim kayıtlı değerle sabitlenir.
              // Aksi halde erken set (voiceData adı) ~1sn sonra API adıyla ezilip değişir.
              setSelectedVoice({
                ...matchedVoice,
                name:
                  realVoiceName(target.voiceData?.voiceName, voiceIdToMatch) || matchedVoice.name,
              });
            } else {
              // Eşleşme yok — ses yüklü sayfada (page 1) değil, ileri sayfada
              // olabilir (grid 12'şer paginate). Önce TÜM sayfalardan çöz
              // (grid'i bozmadan) ki açılışta kartta doğru ses görünsün.
              const resolvedVoice = await resolveVoiceAcrossPages(
                provider,
                lang,
                gend,
                voiceIdToMatch
              );

              if (resolvedVoice) {
                setSelectedVoice({
                  ...resolvedVoice,
                  name:
                    realVoiceName(target.voiceData?.voiceName, voiceIdToMatch) ||
                    resolvedVoice.name,
                });
              } else {
                // Fetch de çözemedi → kurs profil sesiyse voice-profile'dan çöz.
                const isProfileVoice =
                  voiceProfile?.voiceName &&
                  String(voiceProfile.voiceName).toLowerCase().trim() === searchId;

                if (isProfileVoice && voiceProfile?.voiceDetail?.name) {
                  setSelectedVoice({
                    id: voiceIdToMatch,
                    name: voiceProfile.voiceDetail.name,
                    gender: voiceProfile.voiceDetail.gender || gend,
                  });
                } else {
                  // Gerçek ad varsa onu, hiç ad yoksa son çare id göster.
                  let fallbackName =
                    realVoiceName(
                      target.voiceData?.voiceName || metadata.VoiceName,
                      voiceIdToMatch
                    ) || voiceIdToMatch;

                  if (fallbackName && (provider === "azure" || provider === "microsoft")) {
                    fallbackName = cleanLegacyVoiceName(fallbackName) || fallbackName;
                  }

                  setSelectedVoice({
                    id: voiceIdToMatch,
                    name: fallbackName,
                  });
                }
              }
            }
          }

          // Audio URL'i set et
          setAudioUrl(target.file);
          setAudioGeneratedInSession(false);

          // Volume, startTime, endTime'ı set et
          if (target.volumeLevel != null) {
            setVolume(Math.round(target.volumeLevel * 100));
          }
          if (target.startTime != null) {
            setSceneStartTime(target.startTime);
          }
          if (target.endTime != null) {
            setSceneEndTime(target.endTime);
            setDuration(target.endTime - (target.startTime || 0));
          }

          // Yüklenen ses URL'sini kaydet
          loadedSoundUrlRef.current = target.file;
        } else {
          setNarrationText(target.narrationText || "");
          setAudioUrl(target.file);
          setAudioGeneratedInSession(false);
          if (target.volumeLevel != null) {
            setVolume(Math.round(target.volumeLevel * 100));
          }
          if (target.startTime != null) {
            setSceneStartTime(target.startTime);
          }
          if (target.endTime != null) {
            setSceneEndTime(target.endTime);
            setDuration(target.endTime - (target.startTime || 0));
          }
          loadedSoundUrlRef.current = target.file;
          // Ses dosyası var ama voiceData/metadata yok (eski kayıt, sidecar .txt
          // silinmiş vb.) → anlatıcı çözülemiyor. Kartı "?" / "-" bırakmak yerine
          // dosyasız daldaki gibi default anlatıcıyı seçili getir; kullanıcı
          // "Yeniden Oluştur"a basınca üretim yapabilsin.
          loadVoices(defaultSrv, defaultLang, "", 1, defaultVoiceName);
          // Kurs sesini (voiceProfile) ID ile TÜM sayfalardan çöz → kartta göster.
          if (voiceProfile?.voiceName) {
            resolveVoiceAcrossPages(defaultSrv, defaultLang, "", voiceProfile.voiceName).then(
              (v) => {
                if (v) setSelectedVoice(v);
              }
            );
          }
        }

        // Metni Question Generator ile aynı kaynaktan çöz: backend scene-audios
        // narrationTextOriginal önceliklidir; yoksa yukarıda set edilen Redux
        // narrationText kalır. Yeni yüklenen eğitimde Redux narrationText boş
        // gelebildiği için bu adım olmadan kutu boş kalıyordu.
        await loadNarrationText(target);
      },
      [soundList, loadClonedVoices, loadNarrationText]
    );

    // Taslak geri yüklendiyse: normal init (kayıtlı ses / default anlatıcı) atlandı;
    // ses grid'ini taslağın sağlayıcı/diliyle yükle ki anlatıcı seçili görünsün.
    // Sesi zaten soundList'e girmiş eski taslağı da store'dan temizle.
    useEffect(() => {
      const d = getNarrationDraft(navItemSelected);
      if (d && !mountDraft) {
        clearNarrationDraft(navItemSelected);
        return;
      }
      if (!mountDraft) return;
      if (mountDraft.narratorMode === "own") {
        loadClonedVoices();
      } else {
        loadVoices(
          mountDraft.service || "",
          mountDraft.language || "",
          "",
          1,
          mountDraft.voice?.name || mountDraft.voice?.id || ""
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Başka bir instance'ın (sahneden çıkılınca unmount olan) başlattığı üretim
    // bitince sonucu bu instance'a uygula.
    useEffect(() => {
      const apply = () => {
        if (!awaitingDraftRef.current) return;
        const d = getNarrationDraft(navItemSelected);
        if (!d) {
          awaitingDraftRef.current = false;
          setIsGenerating(false);
          return;
        }
        if (d.status !== "ready") return;
        awaitingDraftRef.current = false;
        setAudioUrl(d.audioUrl || null);
        setAudioGeneratedInSession(!!d.audioUrl && !d.edited);
        loadedSoundUrlRef.current = null;
        setIsPlaying(false);
        setCurrentTime(0);
        setProgress(0);
        setDuration(d.duration || 0);
        setSceneEndTime(d.duration || 0);
        setIsGenerating(false);
      };
      apply();
      return subscribeNarrationDrafts(apply);
    }, [navItemSelected]);

    // Track if initial sound has been loaded to prevent re-loading on provider changes
    const hasLoadedInitialSound = useRef(!!mountDraft);

    useEffect(() => {
      // Don't load voices on initial mount - let user select provider and language first
      const init = async () => {
        if (!hasLoadedInitialSound.current) {
          if (selectedSoundIndex != null && soundList && soundList[selectedSoundIndex]) {
            // Existing sound mode: loadSavedSound now resolves narration text
            // itself (Redux first, backend narrationTextOriginal preferred).
            await loadSavedSound(soundList[selectedSoundIndex]);
          } else {
            // selectedSoundIndex=null. Sahnede kayıtlı ses adayı varsa default'u
            // BURADA yükleyip seçme: kart önce default/profil sesini, ~1sn sonra
            // auto-select ile kayıtlı sesi gösterir = flicker. Seçimi (kayıtlı
            // veya default) voiceFilterReady effect'ine bırak — o, kayıtlı sesi
            // doğru provider ile yükler, default grid flash'ı da olmaz.
            // Hiç kayıtlı ses yoksa (gerçek yeni ses) default anlatıcıyı seç.
            const hasSavedCandidates = Array.isArray(soundList) && soundList.length > 0;
            if (!hasSavedCandidates) {
              loadVoices(defaultSrv, defaultLang, "", 1, defaultVoiceName);
            }
          }
          hasLoadedInitialSound.current = true;
        }
      };
      init();
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        if (sampleAudioRef.current) {
          sampleAudioRef.current.pause();
          sampleAudioRef.current = null;
        }
        // Cleanup preview URLs
        if (cloneVoiceFilePreviewUrl) {
          URL.revokeObjectURL(cloneVoiceFilePreviewUrl);
        }
        if (recordingAudioUrl) {
          URL.revokeObjectURL(recordingAudioUrl);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // soundList değiştiğinde kaydedilmiş ses bilgilerini yükle
    // Only trigger when soundList actually changes (not when loadSavedSound function reference changes)
    useEffect(() => {
      // New sound mode (selectedSoundIndex=null) -> do nothing
      if (selectedSoundIndex == null) return;
      if (hasLoadedInitialSound.current && soundList?.length > 0) {
        const entry = soundList[selectedSoundIndex];
        const currentUrl = entry?.file;
        if (currentUrl && loadedSoundUrlRef.current !== currentUrl) {
          loadSavedSound(entry);
        }
      }
    }, [soundList, loadSavedSound]);

    // Volume değiştiğinde audio element'i güncelle
    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.volume = volume / 100;
      }
    }, [volume]);

    // audioUrl değiştiğinde state'i resetle (audio src değişince otomatik yüklenir)
    useEffect(() => {
      if (audioUrl) {
        setCurrentTime(0);
        setProgress(0);
        setIsPlaying(false);
      }
    }, [audioUrl]);

    useEffect(() => {
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause();
        sampleAudioRef.current = null;
      }
      setSamplePlayingId(null);
    }, [voices]);

    const handlePlayPause = useCallback(() => {
      if (!audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Modal içinde ses dosyasının başından başlat
        const totalDuration = sceneEndTime - sceneStartTime;
        if (audioRef.current.currentTime < 0 || audioRef.current.currentTime >= totalDuration) {
          audioRef.current.currentTime = 0;
          setCurrentTime(0);
          setProgress(0);
        }
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, [isPlaying, sceneStartTime, sceneEndTime]);

    const handleTimeUpdate = useCallback(() => {
      if (!audioRef.current) return;
      const cur = audioRef.current.currentTime;
      const dur = audioRef.current.duration || 0;

      // Modal içinde: Audio 0'dan başlayıp (sceneEndTime - sceneStartTime) kadar çalacak
      const totalDuration = sceneEndTime - sceneStartTime;

      setCurrentTime(cur);
      setProgress(totalDuration > 0 ? (cur / totalDuration) * 100 : 0);

      // totalDuration'a ulaşıldığında durdur
      if (totalDuration > 0 && cur >= totalDuration) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        setCurrentTime(0);
        setProgress(0);
      }
    }, [sceneEndTime, sceneStartTime]);

    const handleLoadedMetadata = useCallback(() => {
      if (!audioRef.current) return;
      setDuration(audioRef.current.duration || 0);
    }, []);

    const handleAudioEnded = useCallback(() => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    }, []);

    const handleProgressBarClick = useCallback(
      (e) => {
        if (!audioRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const percentage = (e.clientX - rect.left) / rect.width;

        // Modal içinde 0'dan (sceneEndTime - sceneStartTime) aralığına map et
        const totalDuration = sceneEndTime - sceneStartTime;
        const newTime = percentage * totalDuration;

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
        setProgress(percentage * 100);
      },
      [sceneEndTime, sceneStartTime]
    );

    const handleSamplePlay = useCallback(
      (voice) => {
        // Yeni API response formatına göre sample URL'i al
        let sampleUrl = null;

        if (voice.sampleUrlsByLanguage) {
          const availableLanguages = Object.keys(voice.sampleUrlsByLanguage);

          // Öncelik sırası: 1. Türkçe, 2. İngilizce, 3. Diğerleri
          const getPrioritizedLanguage = () => {
            // Manuel seçilen sample dili varsa öncelik ver
            if (sampleLanguage && availableLanguages.includes(sampleLanguage)) {
              return sampleLanguage;
            }

            // 1. Öncelik: o an açık olan editör dili (i18n.language)
            for (const lang of editorLanguageSampleCandidates()) {
              if (availableLanguages.includes(lang)) {
                return lang;
              }
            }

            // 2. Öncelik: Türkçe (tr-TR, tr)
            const turkishLangs = ["tr-TR", "tr"];
            for (const lang of turkishLangs) {
              if (availableLanguages.includes(lang)) {
                return lang;
              }
            }

            // 3. Öncelik: İngilizce (en-US, en-GB, en)
            const englishLangs = ["en-US", "en-GB", "en"];
            for (const lang of englishLangs) {
              if (availableLanguages.includes(lang)) {
                return lang;
              }
            }

            // 4. Seçili dil varsa onu kullan
            if (selectedLanguage && availableLanguages.includes(selectedLanguage)) {
              return selectedLanguage;
            }

            // 5. İlk available dil
            return availableLanguages[0];
          };

          const sampleLang = getPrioritizedLanguage();

          const languageSamples = voice.sampleUrlsByLanguage[sampleLang];
          if (languageSamples && languageSamples.v1) {
            sampleUrl = languageSamples.v1;
          }
        }

        // Fallback: eski format için
        if (!sampleUrl) {
          sampleUrl = voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl;
        }

        if (!sampleUrl) return;

        // Stop if same voice is playing
        if (samplePlayingId === voice.id && sampleAudioRef.current) {
          sampleAudioRef.current.pause();
          sampleAudioRef.current = null;
          setSamplePlayingId(null);
          return;
        }

        if (sampleAudioRef.current) {
          sampleAudioRef.current.pause();
        }

        const audio = new Audio(sampleUrl);
        sampleAudioRef.current = audio;
        setSamplePlayingId(voice.id);
        audio.onended = () => {
          setSamplePlayingId(null);
          sampleAudioRef.current = null;
        };
        audio.play().catch(() => {
          setSamplePlayingId(null);
          sampleAudioRef.current = null;
        });
      },
      [samplePlayingId, selectedLanguage, sampleLanguage]
    );

    // "Ses Oluştur / Yeniden Oluştur" ön koşulu: seçili bir anlatıcı olmalı.
    // Provider değiştirilince selectedVoice sıfırlanıyor (handleServiceChange), yani
    // yeni ses listesi yüklenirken hiçbir anlatıcının seçili OLMADIĞI bir aralık var;
    // butonu o aralıkta kilitliyoruz ki kullanıcı boşa tıklayıp hata mesajı almasın.
    // ("ai" modunda sağlayıcı da gerekli: "Tümü" filtresinde ses seçilince
    // handleServiceChange değil, kart onClick'i selectedService'i dolduruyor.)
    const narratorSelected =
      narratorMode === "own" ? !!selectedVoice?.id : !!selectedService && !!selectedVoice?.id;

    const generateTTS = useCallback(async () => {
      setValidationError("");
      if (!narrationText.trim()) {
        setValidationError(i18n.t("YouMustEnterAVoiceoverText") || "Lütfen ses metni giriniz.");
        return;
      }

      // Narrator not selected -> warn and open panel. Buton bu durumda zaten
      // kilitli (narratorSelected); bu dal klavye/programatik çağrılar için kalıyor.
      if (!narratorSelected) {
        setValidationError(i18n.t("PleaseSelectNarrator"));
        setProfileOpen(true);
        return;
      }

      // Taslak: üretim başladığı anda sahneye yaz. Kullanıcı başka sahneye geçse
      // de istek/poll sürer ve sonuç store'a düşer; geri gelince taslak görünür.
      const draftNavId = navItemSelected;
      const prevDraft = getNarrationDraft(draftNavId);
      let draftSettled = false;
      const beginDraft = () =>
        replaceNarrationDraft(draftNavId, {
          status: "generating",
          text: narrationText,
          originalText: prevDraft?.originalText ?? baselineTextRef.current ?? narrationText,
          edited: false,
          audioUrl: null,
          duration: 0,
          soundIndex: selectedSoundIndex,
          soundFile:
            selectedSoundIndex != null ? soundList?.[selectedSoundIndex]?.file || null : null,
          voice: selectedVoice,
          service: selectedService,
          language: selectedLanguage,
          narratorMode,
          elevenLabsModel: selectedElevenLabsModel,
          googleModel: selectedGoogleModel,
          geminiStyle,
          geminiPace,
          geminiAudioProfile,
          // "Tümünü onayla" bu sahne mount değilken de aynı girişi kurabilsin.
          startTime: Number(sceneStartTime) || 0,
          volumeLevel: volume / 100,
          soundName: activeRestoreIdx != null && restoredSoundName ? restoredSoundName : null,
        });
      const finishDraft = (url, dur) => {
        draftSettled = true;
        setNarrationDraft(draftNavId, { status: "ready", audioUrl: url, duration: dur || 0 });
      };
      // Hata: önceki (hazır) taslak varsa ona dön, yoksa taslağı kaldır.
      const failDraft = () => {
        if (draftSettled) return;
        draftSettled = true;
        replaceNarrationDraft(draftNavId, prevDraft?.status === "ready" ? prevDraft : null);
      };

      // Own voice mode - use cloned voice
      if (narratorMode === "own") {
        if (!selectedVoice?.id) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("PleaseSelectClonedVoice") || "Please select a cloned voice") +
              "</span>",
            false
          );
          return;
        }
        beginDraft();
        const cloned = await generateTTSWithClonedVoice(narrationText, selectedVoice.id);
        if (cloned?.fileUrl) finishDraft(cloned.fileUrl, cloned.duration);
        else failDraft();
        return;
      }

      // AI mode'da sağlayıcı, dil ve ses kontrolü
      if (narratorMode === "ai") {
        if (!selectedService) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" +
              (i18n.t("PleaseSelectProvider") || "Lütfen bir sağlayıcı seçiniz.") +
              "</span>",
            false
          );
          return;
        }
        // ElevenLabs ve Google (Gemini) için dil kontrolü yapma
        if (selectedService !== "elevenlabs" && selectedService !== "google" && !selectedLanguage) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("PleaseSelectLanguage") || "Lütfen bir dil seçiniz.") + "</span>",
            false
          );
          return;
        }
        if (!selectedVoice?.id) {
          onServerModal?.(
            i18n.t("error.warning"),
            "<span>" + (i18n.t("PleaseSelectVoice") || "Lütfen bir ses seçiniz.") + "</span>",
            false
          );
          return;
        }
      }

      const providerMap = { elevenlabs: "elevenlabs", azure: "azure", google: "gemini" };
      const provider = providerMap[selectedService] || selectedService;
      setIsGenerating(true);
      beginDraft();
      try {
        const requestBody = {
          idealStudioTrainingId: window.idealStudioTrainingId,
          provider,
          text: narrationText.trim(),
          language: provider === "elevenlabs" ? "multilingual" : selectedLanguage,
          voice: selectedVoice?.id || voiceProfile?.voiceName || "",
          // Routing hints so the async result lands on the right scene (and the
          // activity widget can drop it in if this modal is closed first).
          navId: navItemSelected,
          sceneIndex: String(sceneIndex),
        };

        if (provider === "gemini-flash-3") {
          if (geminiStyle) requestBody.style = geminiStyle;
          if (geminiPace) requestBody.pace = geminiPace;
          if (geminiAudioProfile.trim()) requestBody.audioProfile = geminiAudioProfile.trim();
        }

        // ElevenLabs model seçimi ('v2_5' | 'v3'). Dil GÖNDERİLMEZ — backend
        // course dilini training kaydından okur ve v2.5 ise language_code olarak
        // uygular. v3'te language_code hiç gönderilmez.
        if (provider === "elevenlabs") {
          requestBody.elevenLabsModel = selectedElevenLabsModel;
        }

        // The endpoint is async: 202 { jobId } now, audio later. Submit, then
        // poll the job-status endpoint until it completes.
        const response = await fetch(`${process.env.MW_API_URL}api/course/text-to-speech`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(requestBody),
        });
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          const jobId = data.jobId;
          if (!jobId) {
            onServerModal?.(
              i18n.t("FileModal.APIProviders.error"),
              "<span>" + (i18n.t("error.serviceError") || "Service Error") + "</span>",
              false
            );
            return;
          }
          // Spinner stays up while we poll the job until the worker finishes
          // and the audio is ready (or the waiter times out).
          const result = await waitForTtsJob(jobId);
          generatedTtsJobRef.current = { jobId, audioUrl: result.audioUrl };
          finishDraft(result.audioUrl, result.duration);
          setAudioUrl(result.audioUrl);
          setAudioGeneratedInSession(true);
          loadedSoundUrlRef.current = null; // Reset ref when new audio is generated
          setIsPlaying(false);
          setCurrentTime(0);
          setProgress(0);
          setDuration(result.duration || 0);
          setSceneEndTime(result.duration || 0);
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (checkInsufficientBalance(errorData)) {
            return;
          }
          onServerModal?.(
            i18n.t("FileModal.APIProviders.error"),
            "<span>" + (i18n.t("error.systemError") || "System Error") + "</span>",
            false
          );
        }
      } catch (err) {
        console.error("TTS generate error:", err);
        onServerModal?.(
          i18n.t("FileModal.APIProviders.error"),
          "<span>" + (i18n.t("error.systemError") || "System Error") + "</span>",
          false
        );
      } finally {
        failDraft();
        setIsGenerating(false);
      }
    }, [
      narrationText,
      selectedSoundIndex,
      soundList,
      sceneStartTime,
      volume,
      activeRestoreIdx,
      restoredSoundName,
      selectedService,
      selectedGoogleModel,
      selectedLanguage,
      selectedElevenLabsModel,
      selectedVoice,
      voiceProfile,
      onServerModal,
      narratorMode,
      geminiStyle,
      geminiPace,
      geminiAudioProfile,
      checkInsufficientBalance,
      navItemSelected,
      sceneIndex,
      narratorSelected,
    ]);

    const handleServiceChange = (newService) => {
      setSelectedService(newService);
      setVoices([]);
      setCurrentPage(1);
      setTotalPages(1);

      // Provider değiştiğinde seçili sesi temizle
      setSelectedVoice(null);

      // Google dışına çıkıldığında model seçimini default'a çek
      if (newService !== "google") {
        setSelectedGoogleModel(DEFAULT_GOOGLE_MODEL);
        setGeminiStyle("");
        setGeminiPace("");
        setGeminiAudioProfile("");
      }

      // ElevenLabs dışına çıkıldığında model seçimini default'a sıfırla.
      if (newService !== "elevenlabs") {
        setSelectedElevenLabsModel(DEFAULT_ELEVENLABS_MODEL);
      }

      // ElevenLabs için otomatik multilingual seç, diğerleri için filtreleri temizle
      if (newService === "elevenlabs") {
        setSelectedLanguage("multilingual");
        loadVoices(newService, "multilingual", "", 1, "");
      } else if (newService) {
        // Diğer providerlar için dil ve cinsiyet filtrelerini temizle
        // Kullanıcı isterse sonra seçecek
        setSelectedLanguage("");
        setSelectedGender("");
        loadVoices(newService, "", "", 1, "");
      } else {
        // "Tümü" (All) — tüm sağlayıcıların seslerini yükle (service paramı yok).
        setSelectedLanguage("");
        setSelectedGender("");
        loadVoices("", "", "", 1, "");
      }
    };

    // Mevcut kaydetme davranışı: ses verisini bu sahneye ekle/güncelle.
    // keepOpen: taslağı tek başına onaylarken modal açık kalsın (başarı kartı
    // modalı kapatır → diğer sahnelerin taslaklarına geçilemezdi).
    const commitSound = useCallback(
      (soundData, { keepOpen = false } = {}) => {
        // Kaydedilen ses artık taslak değil. onAddSound soundList'i değiştirip
        // bu bileşeni remount ettirdiği için temizlik ondan ÖNCE.
        clearNarrationDraft(navItemSelected);
        const generated = generatedTtsJobRef.current;
        if (generated && generated.audioUrl === soundData?.file) {
          generatedTtsJobRef.current = null;
          approveJob(generated.jobId);
        }
        onAddSound?.(soundData, selectedSoundIndex != null ? selectedSoundIndex : null, !keepOpen);
        if (keepOpen) setAudioGeneratedInSession(false);
      },
      [onAddSound, selectedSoundIndex, navItemSelected]
    );

    const handleAddSound = useCallback(
      ({ keepOpen = false } = {}) => {
        // Validation
        if (!audioUrl) {
          setValidationError(i18n.t("PleaseGenerateVoiceFirst") || "Lütfen önce ses oluşturun");
          return;
        }

        const effectiveStart =
          Number(sceneStartTime) > 0
            ? Number(sceneStartTime)
            : (getEditorPlayhead()?.frame || 0) / 30;

        const soundData = buildNarrationSoundData({
          file: audioUrl,
          // Geri yüklenen ses orijinal adını korur; aksi halde standart ad üretilir.
          name:
            activeRestoreIdx != null && restoredSoundName
              ? restoredSoundName
              : `Scene-${sceneIndex}-narration.mp3`,
          startTime: effectiveStart,
          duration,
          volumeLevel: volume / 100,
          narrationText,
          service: selectedService,
          language: selectedLanguage,
          elevenLabsModel: selectedElevenLabsModel,
          voice: selectedVoice,
          narratorMode,
          googleModel: selectedGoogleModel,
          geminiStyle,
          geminiPace,
          geminiAudioProfile,
        });

        // Kullanıcı "Tüm sahneleri seslendir" kutusunu işaretlediyse ve bu ses buna
        // uygunsa (asıl anlatım + AI provider), kaydetmeden önce onay sor. Kutu
        // işaretli değilse popup çıkmaz; doğrudan bu sahneye kaydedilir.
        // Taslak notundaki "Onayla" (keepOpen) yalnızca bu sahneyi onaylar.
        if (!keepOpen && narrateAllScenes && eligibleForAllScenes) {
          pendingSoundDataRef.current = soundData;
          setConfirmAllOpen(true);
          return;
        }

        commitSound(soundData, { keepOpen });
      },
      [
        audioUrl,
        narrationText,
        sceneStartTime,
        sceneEndTime,
        volume,
        sceneIndex,
        selectedService,
        selectedGoogleModel,
        selectedLanguage,
        selectedElevenLabsModel,
        selectedVoice,
        narratorMode,
        geminiStyle,
        geminiPace,
        geminiAudioProfile,
        commitSound,
        eligibleForAllScenes,
        narrateAllScenes,
        activeRestoreIdx,
        restoredSoundName,
      ]
    );

    // Onay popup'ı: "Tüm sahneler" — seçili sesi tüm sahnelere uygula (batch narrate-all-scenes).
    // Her sahnenin metni frontend'den gönderilir: aktif sahne için canlı metin,
    // diğerleri için kayıtlı narrationText. 202 + jobId döner; iş arka planda sürer.
    const handleApplyToAllScenes = useCallback(async () => {
      setIsNarratingAll(true);

      // Her sahnenin metni gerekli. Kaynaklar (öncelik sırası):
      //  1) Aktif sahne için ekrandaki canlı metin,
      //  2) Bu oturumda düzenlenmiş diğer sahneler için Redux soundList narrationText,
      //  3) Kaydedilmiş narrationTextOriginal (scene-audios endpoint) — yeni yüklenmiş
      //     eğitimde soundList narrationText taşımadığı için asıl kaynak budur.
      const persistedTextByNav = {};
      try {
        const data = await SceneAudioService.getSceneAudios();
        const sceneList = Array.isArray(data?.scenes) ? data.scenes : [];
        for (const s of sceneList) {
          if (!s?.navId || !Array.isArray(s.audios)) continue;
          const withText =
            s.audios.find((a) => a?.narrationTextOriginal) ||
            s.audios.find((a) => a?.narrationText);
          const t = withText?.narrationTextOriginal || withText?.narrationText || "";
          if (t) persistedTextByNav[s.navId] = t;
        }
      } catch (error) {
        console.error("narrate-all-scenes: getSceneAudios failed", error);
      }

      const reduxTextFor = (navId) => {
        const list = (allSoundLists && allSoundLists[navId]) || [];
        const entry = list.find((s) => s && s.narrationText);
        return entry?.narrationText || "";
      };

      let ids = Array.isArray(navItemsIds) ? navItemsIds : [];
      if (ids.length === 0) ids = Object.keys(persistedTextByNav);

      const scenes = [];
      for (const navId of ids) {
        let text;
        if (navId === navItemSelected) {
          text = narrationText || reduxTextFor(navId) || persistedTextByNav[navId];
        } else {
          text = reduxTextFor(navId) || persistedTextByNav[navId];
        }
        text = (text || "").trim();
        if (!text) continue;

        const scene = { navId, text: text.slice(0, 5000) };
        // SADECE aktif sahnenin bu oturumda yeni üretilen sesi için audioUrl gönder.
        // Kullanıcı bu sahneyi seslendirip kaydete bastığında ses zaten oluştu;
        // URL'i de yollayınca backend bu sahneyi tekrar üretmez (echo) ve ikinci
        // kez faturalandırmaz. Diğer sahneler URL'siz gider ve üretilir.
        if (navId === navItemSelected && audioGeneratedInSession && audioUrl) {
          scene.audioUrl = audioUrl;
        }
        scenes.push(scene);
      }

      if (scenes.length === 0) {
        setIsNarratingAll(false);
        onServerModal?.(
          i18n.t("error.warning"),
          `<span>${
            i18n.t("NarrateScope.NoScenes") || "Seslendirilecek metni olan sahne bulunamadı."
          }</span>`,
          false
        );
        return;
      }

      const providerMap = { elevenlabs: "elevenlabs", azure: "azure", google: "gemini" };
      // Klon (own) ses ElevenLabs tabanlı: batch'e provider "elevenlabs", voice =
      // klonun providerId'si (ElevenLabs voice id) gider — id değil. AI ElevenLabs
      // ile aynı kontrat, sadece ses klon.
      const isOwn = narratorMode === "own";
      const provider = isOwn ? "elevenlabs" : providerMap[selectedService] || selectedService;
      const language = provider === "elevenlabs" ? "multilingual" : selectedLanguage;
      const voice = isOwn ? selectedVoice?.providerId || "" : selectedVoice?.id || "";
      // Yeni sesin görünen adı/cinsiyeti de taşınır ki batch sonrası soundList'e
      // yazılan voiceData eski ismi tutmasın (flicker'ın asıl kaynağı buydu).
      const voiceName = selectedVoice?.name || "";
      const voiceGender = selectedVoice?.gender || "";

      const baseUrl = window.ideaLibraryRootUrl?.endsWith("/")
        ? window.ideaLibraryRootUrl
        : (window.ideaLibraryRootUrl || "") + "/";

      try {
        const response = await fetch(`${baseUrl}api/ideal-studio/training/narrate-all-scenes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            trainingId: window.idealStudioTrainingId,
            scenes,
            provider,
            voice,
            language,
          }),
        });

        if (response.ok) {
          // 202 Accepted — job kuyruğa alındı. jobId'yi arka plan takipçisine
          // (NarrationJobTracker) bir window event ile devret; modal kapanınca da
          // poll + sonuçları Redux'a uygulama orada sürer.
          const data = await response.json().catch(() => ({}));
          const jobId = data?.jobId;
          clearNarrationDraft(navItemSelected);
          setConfirmAllOpen(false);
          pendingSoundDataRef.current = null;
          if (jobId) {
            window.dispatchEvent(
              new CustomEvent("narrateAllScenesStarted", {
                detail: {
                  jobId,
                  baseUrl,
                  trainingId: window.idealStudioTrainingId,
                  scenes,
                  provider,
                  voice,
                  voiceName,
                  voiceGender,
                  language,
                },
              })
            );
          } else {
            onServerModal?.(
              i18n.t("NarrateScope.StartedTitle") || "Seslendirme başlatıldı",
              `<span>${
                i18n.t("NarrateScope.Started", { count: scenes.length }) ||
                `Seslendirme işlemi başlatıldı (${scenes.length} sahne). İşlem arka planda tamamlanacak.`
              }</span>`,
              false
            );
          }
          onCancel?.();
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (checkInsufficientBalance(errorData)) {
            return;
          }
          onServerModal?.(
            i18n.t("error.warning"),
            `<span>${
              errorData.message ||
              i18n.t("NarrateScope.Error") ||
              "Seslendirme başlatılamadı. Lütfen tekrar deneyin."
            }</span>`,
            false
          );
        }
      } catch (err) {
        console.error("narrate-all-scenes error:", err);
        onServerModal?.(
          i18n.t("error.warning"),
          `<span>${
            i18n.t("NarrateScope.Error") || "Seslendirme başlatılamadı. Lütfen tekrar deneyin."
          }</span>`,
          false
        );
      } finally {
        setIsNarratingAll(false);
      }
    }, [
      navItemsIds,
      navItemSelected,
      narrationText,
      audioUrl,
      audioGeneratedInSession,
      allSoundLists,
      narratorMode,
      selectedService,
      selectedLanguage,
      selectedVoice,
      onServerModal,
      onCancel,
      checkInsufficientBalance,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        handleAddSound,
      }),
      [handleAddSound]
    );

    const checkCredit = useCallback(async () => {
      const providerMap = { elevenlabs: "elevenlabs", azure: "azure", google: "gemini" };
      const provider = providerMap[selectedService] || selectedService;
      const apiUrl = process.env.MW_API_URL;
      const body = {
        text: narrationText.trim() || "kredi tahmin",
        provider,
        voice: selectedVoice?.id || "",
        language: provider === "elevenlabs" ? "multilingual" : selectedLanguage,
      };
      const res = await fetch(`${apiUrl}api/course/text-to-speech/credit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Credit check failed");
      }
      const data = await res.json();
      return {
        requiredCredits: data.requiredCredits,
        availableCredits: data.availableCredits,
        hasSufficientBalance: data.hasSufficientBalance,
        providers: [{ label: provider, cost: data.requiredCredits }],
      };
    }, [selectedService, narrationText, selectedVoice, selectedLanguage]);

    useEffect(() => {
      onCreditCheckReady?.(checkCredit);
    }, [checkCredit, onCreditCheckReady]);

    // Prefill from the agent's `prepareNarration` tool result. Applies the
    // narration text (prompt) and, best-effort, the voice/service/language from
    // settings when provided. applyFn is memoized so usePopupPrefill's effect
    // does not re-run on every render.
    const applyNarrationIntent = useCallback(
      (payload) => {
        if (!payload) return;
        if (typeof payload.prompt === "string" && payload.prompt.trim()) {
          setNarrationText(payload.prompt.trim());
        }
        const s = payload.settings;
        if (s && typeof s === "object") {
          if (typeof s.service === "string" && s.service) {
            const svc = SERVICE_MAP[s.service.toLowerCase()] || s.service.toLowerCase();
            setSelectedService(svc);
            // Auto-load voices for the prefilled service.
            const lang =
              typeof s.language === "string" && s.language
                ? normalizeLanguageCode(s.language)
                : svc === "elevenlabs"
                  ? "multilingual"
                  : "";
            const voiceName = typeof s.voice === "string" ? s.voice : "";
            setSelectedLanguage(lang);
            loadVoices(svc, lang, "", 1, voiceName);
          } else if (typeof s.voice === "string" && s.voice) {
            // No service in settings but there's a voice name — best-effort load
            // with the current service.
            loadVoices(selectedService, selectedLanguage, "", 1, s.voice);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [loadVoices, selectedService, selectedLanguage]
    );
    usePopupPrefill("sound", applyNarrationIntent);

    // Custom dropdown: dışarı tıklayınca kapan.
    useEffect(() => {
      if (!selectorOpen) return;
      const onDoc = (e) => {
        if (selectorRef.current && !selectorRef.current.contains(e.target)) {
          setSelectorOpen(false);
        }
      };
      document.addEventListener("mousedown", onDoc);
      return () => document.removeEventListener("mousedown", onDoc);
    }, [selectorOpen]);

    // Silinmiş sahne sesini geri getir: server kaydından metin+ses+voice'i
    // "yeni klip" moduna getir (selectedSoundIndex=null → Kaydet'te timeline'a
    // yeni klip eklenir, mevcudu ezmez). Ad "Narration - <sahne adı>" olur.
    const restoreCandidate = useCallback(
      (ri) => {
        const cand = restoreCandidates[ri];
        if (!cand) return;
        setActiveRestoreIdx(ri);
        const fname = cand.filePath
          ? decodeURIComponent(cand.filePath.split("/").pop().split("?")[0]).replace(
              /\.(mp3|wav|ogg|webm|m4a|aac|flac|wma)$/i,
              ""
            )
          : "";
        const title = sceneName || cand.sceneTitle || "";
        setRestoredSoundName(title ? `Narration - ${title}` : cand.name || fname || "");
        setSelectedSoundIndex(null);
        setAudioGeneratedInSession(false);
        loadedSoundUrlRef.current = null;
        // loadSavedSound .txt metadata'sından metni VoiceText (SSML'li olabilir)
        // ile ezebilir; kullanıcı ORİJİNAL metni istiyor → yüklemeden SONRA bas.
        Promise.resolve(
          loadSavedSound({ file: cand.filePath, narrationText: cand.narrationTextOriginal })
        ).finally(() => {
          setNarrationText(cand.narrationTextOriginal || "");
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [restoreCandidates, sceneName, loadSavedSound]
    );

    /**
     * "Yeni anlatım sesi oluştur" akışı.
     *
     * Eskiden yalnızca ses seçme dropdown'ının son satırındaydı; artık liste
     * DOLUYSA sağ üstteki + düğmesinden, liste BOŞSA (dropdown'da başka bir şey
     * yokken çıkmaz sokak olmasın diye) yine dropdown girişinden çağrılıyor.
     * İki çağırıcı olduğu için gövde buraya taşındı — davranış birebir aynı.
     *
     * Sahnede o an seçili olan voice korunuyor: sabit default anlatıcıya
     * sıfırlamak yerine voice/service/language/narratorMode aynı kalıyor,
     * yalnızca klibe özel alanlar (metin, ses dosyası, zaman, ses seviyesi)
     * temizleniyor.
     */
    const handleCreateNewNarration = () => {
      const keepVoice = selectedVoice;
      const keepService = selectedService;
      const keepLanguage = selectedLanguage;
      const keepMode = narratorMode;
      const keepGender = selectedGender;
      setActiveRestoreIdx(null);
      setRestoredSoundName("");
      setSelectedSoundIndex(null);
      baselineTextRef.current = null;
      setShowOriginalText(false);
      setAudioUrl(null);
      setAudioGeneratedInSession(false);
      setNarrationText("");
      setSceneStartTime(0);
      setSceneEndTime(0);
      setVolume(56);
      loadedSoundUrlRef.current = null;
      if (keepVoice?.id || keepVoice?.name) {
        // Sahnedeki voice korunur; grid'i o servis/dil ile yükleyip
        // aynı sesi seçili göster (klon seste selectedVoice zaten kalır).
        if (keepMode === "own") {
          loadClonedVoices();
        } else {
          loadVoices(
            keepService,
            keepLanguage,
            keepGender || "",
            1,
            keepVoice.name || keepVoice.id
          );
        }
      } else {
        // Sahnede seçili ses yoksa default anlatıcıya düş.
        setSelectedService(defaultSrv);
        setSelectedLanguage(defaultLang);
        setNarratorMode("ai");
        setSelectedVoice(null);
        loadVoices(defaultSrv, defaultLang, "", 1, defaultVoiceName);
      }
      setSelectorOpen(false);
    };

    // Sağ üstteki + düğmesi yalnızca listede kayıt VARKEN görünür. Liste boşken
    // görünmesi gereksiz: o durumda select'in kendisi zaten "＋ Yeni Anlatım
    // Sesi Oluştur" seçili görünüyor ve akış oradan devam ediyor.
    // soundList bir prop ve tanımsız gelebiliyor (dosyanın başka yerlerinde de
    // `!soundList ||` ile korunuyor), o yüzden doğrudan .length okunmuyor.
    const hasSavedNarrations = !!soundList && soundList.length > 0;

    // Ekranda görünen, bu sahnenin kaydedilmemiş taslağı mı? (Üretim sürüyor ya
    // da üretilmiş taslak ses yüklü.) Kullanıcı dropdown'dan başka bir kayda
    // geçtiyse taslak store'da kalır ama bu görünüm taslak sayılmaz.
    const isDraftView =
      !!narrationDraft &&
      (narrationDraft.status === "generating"
        ? isGenerating
        : !!narrationDraft.audioUrl && audioUrl === narrationDraft.audioUrl);
    const draftOriginalText = isDraftView ? narrationDraft.originalText || "" : "";
    const draftHasOriginal = isDraftView && draftOriginalText.trim() !== narrationText.trim();
    const viewingOriginal = showOriginalText && draftHasOriginal;

    // "Taslağı at": taslağı sil, metni orijinale; kayıtlı bir sese aitse onu
    // yeniden yükle, yeni sesse oynatıcıyı boşalt.
    const handleDiscardDraft = () => {
      if (!narrationDraft || isGenerating) return;
      const original = narrationDraft.originalText || "";
      clearNarrationDraft(navItemSelected);
      setShowOriginalText(false);
      setValidationError("");
      setAudioGeneratedInSession(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      if (selectedSoundIndex != null && soundList?.[selectedSoundIndex]) {
        loadedSoundUrlRef.current = null;
        Promise.resolve(loadSavedSound(soundList[selectedSoundIndex])).finally(() =>
          setNarrationText(original)
        );
      } else {
        setAudioUrl(null);
        setDuration(0);
        setSceneEndTime(0);
        setNarrationText(original);
        baselineTextRef.current = null;
      }
    };

    return (
      <>
        <div className="add-sound-modal__ai-voice">
          <div className="add-sound-modal__sound-selector">
            {/* Etiket solda, "yeni oluştur" düğmesi sağ üstte. Düğme select'in
                İÇİNDEN buraya taşındı (istek) — dropdown yalnızca var olan
                kayıtları listeliyor, oluşturma eylemi ayrı bir kontrol. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <label className="add-sound-modal__sound-selector-label">
                {i18n.t("SceneAudio.Title") || "Scene Audio"}
              </label>
              {hasSavedNarrations && (
                <button
                  type="button"
                  onClick={handleCreateNewNarration}
                  title={i18n.t("CreateNewNarration") || "Create new voice"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 10px 4px 7px",
                    border: "1px solid #ddd6fe",
                    borderRadius: "999px",
                    background: "#f5f3ff",
                    color: "#7c3aed",
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span aria-hidden="true" style={{ fontSize: "14px", lineHeight: 1 }}>
                    ＋
                  </span>
                  {i18n.t("CreateNewNarration") || "Create new voice"}
                </button>
              )}
            </div>
            {/* Custom dropdown: native <select> option içine buton konamadığı
                için özel liste. Silinmiş anlatım satırı KIRMIZI arka plan +
                "Geri getir" butonu; isim değişmez (kırmızı = silinmiş sinyali). */}
            <div
              className="add-sound-modal__sound-selector-dd"
              ref={selectorRef}
              style={{ position: "relative" }}
            >
              <div
                className="add-sound-modal__sound-selector-select"
                role="button"
                tabIndex={0}
                onClick={() => setSelectorOpen((o) => !o)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectorOpen((o) => !o);
                  }
                }}
              >
                {activeRestoreIdx != null
                  ? restoredSoundName || (sceneName ? `Narration - ${sceneName}` : "-")
                  : selectedSoundIndex != null && soundList[selectedSoundIndex]
                    ? `${selectedSoundIndex + 1}. ${
                        soundList[selectedSoundIndex].name ||
                        (soundList[selectedSoundIndex].file
                          ? soundList[selectedSoundIndex].file.split("/").pop().split("?")[0]
                          : `Sound ${selectedSoundIndex + 1}`)
                      }`
                    : `＋ ${i18n.t("CreateNewNarration") || "Create new voice"}`}
              </div>

              {selectorOpen && (
                <div
                  className="add-sound-modal__sound-selector-menu"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    boxShadow: "0 12px 32px rgba(15,23,42,0.16)",
                    padding: "6px",
                    maxHeight: "280px",
                    overflowY: "auto",
                  }}
                >
                  {soundList.map((s, idx) => {
                    if (!validAIVoiceIndices.includes(idx)) return null;
                    const label =
                      s.name ||
                      (s.file ? s.file.split("/").pop().split("?")[0] : null) ||
                      `Sound ${idx + 1}`;
                    const isSel = activeRestoreIdx == null && selectedSoundIndex === idx;
                    return (
                      <div
                        key={idx}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setActiveRestoreIdx(null);
                          setRestoredSoundName("");
                          setSelectedSoundIndex(idx);
                          setAudioGeneratedInSession(false);
                          loadedSoundUrlRef.current = null;
                          loadSavedSound(soundList[idx]);
                          setSelectorOpen(false);
                        }}
                        onMouseEnter={(e) => {
                          if (!isSel) e.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSel) e.currentTarget.style.background = "transparent";
                        }}
                        style={{
                          padding: "9px 12px",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#1f2937",
                          cursor: "pointer",
                          background: isSel ? "#f1f5f9" : "transparent",
                        }}
                      >
                        {idx + 1}. {label}
                      </div>
                    );
                  })}

                  {/* Silinmiş anlatımlar — kırmızı satır + Geri getir butonu */}
                  {restoreCandidates.map((c, i) => {
                    const title = sceneName || c.sceneTitle || "";
                    const fname = c.filePath
                      ? decodeURIComponent(c.filePath.split("/").pop().split("?")[0]).replace(
                          /\.(mp3|wav|ogg|webm|m4a|aac|flac|wma)$/i,
                          ""
                        )
                      : "";
                    const label = title ? `Narration - ${title}` : fname || `#${i + 1}`;
                    const isActive = activeRestoreIdx === i;
                    return (
                      <div
                        key={`deleted-${i}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                          padding: "8px 12px",
                          margin: "2px 0",
                          borderRadius: "8px",
                          background: "#dc2626",
                        }}
                      >
                        <span
                          style={{
                            color: "#fff",
                            fontSize: "14px",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={label}
                        >
                          {label}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            restoreCandidate(i);
                            setSelectorOpen(false);
                          }}
                          style={{
                            flexShrink: 0,
                            padding: "4px 12px",
                            fontSize: "12px",
                            fontWeight: 600,
                            border: "none",
                            borderRadius: "4px",
                            background: "#fff",
                            color: "#dc2626",
                            cursor: "pointer",
                            opacity: isActive ? 0.7 : 1,
                          }}
                        >
                          ↩ {i18n.t("RestoreDeletedAudio") || "Geri getir"}
                        </button>
                      </div>
                    );
                  })}

                  {/* Kayıtlı anlatım YOKKEN burada kalıyor: aksi halde dropdown
                      tamamen boş açılır ve kullanıcı çıkmaz sokağa girer. Kayıt
                      varsa bu eylem sağ üstteki + düğmesinde. */}
                  {!hasSavedNarrations && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleCreateNewNarration}
                      style={{
                        padding: "9px 12px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#7c3aed",
                        cursor: "pointer",
                      }}
                    >
                      ＋ {i18n.t("CreateNewNarration") || "Create new voice"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="add-sound-modal__ai-voice-card">
            <div className="add-sound-modal__ai-voice-field">
              <div className="add-sound-modal__ai-voice-label-row">
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span className="add-sound-modal__ai-voice-title">
                    {i18n.t("VoiceText") || "Voice Text"} *
                  </span>
                  {isDraftView && (
                    <span className="add-sound-modal__draft-badge">
                      {i18n.t("NarrationDraft.Badge") || "Taslak"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowSSML(!showSSML)}
                    style={{
                      padding: "3px 8px",
                      fontSize: "10px",
                      fontWeight: "600",
                      border: "1px solid #e2e8f0",
                      borderRadius: "4px",
                      background: showSSML ? "#7c3aed" : "#f8fafc",
                      color: showSSML ? "#fff" : "#64748b",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {i18n.t("VoiceTags") || "Voice Tags"}
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {draftHasOriginal && (
                    <button
                      type="button"
                      className={`add-sound-modal__draft-action${viewingOriginal ? " add-sound-modal__draft-action--on" : ""}`}
                      onClick={() => setShowOriginalText((v) => !v)}
                    >
                      {viewingOriginal
                        ? i18n.t("NarrationDraft.ShowDraft") || "Taslağı göster"
                        : i18n.t("NarrationDraft.ShowOriginal") || "Orijinali göster"}
                    </button>
                  )}
                  <span className="add-sound-modal__ai-voice-count">
                    {(viewingOriginal ? draftOriginalText : narrationText).length}{" "}
                    {i18n.t("chars") || "chars"}
                  </span>
                </div>
              </div>
              {isDraftView && (
                <div className="add-sound-modal__draft-note">
                  <span>
                    {narrationDraft.status === "generating"
                      ? i18n.t("NarrationDraft.GeneratingInfo") ||
                        "Ses arka planda oluşturuluyor. Başka sahneye geçebilirsiniz; hazır olunca burada taslak olarak bekler."
                      : narrationDraft.edited
                        ? i18n.t("NarrationDraft.EditedInfo") ||
                          "Metin ses üretildikten sonra değişti. Onaylamak için sesi yeniden oluşturun."
                        : i18n.t("NarrationDraft.ReadyInfo") ||
                          "Düzenlenmiş metin ve ses taslakta bekliyor. Bu sahneyi onaylayın veya taslağı atın."}
                  </span>
                  {narrationDraft.status === "ready" && (
                    <span className="add-sound-modal__draft-actions">
                      <button
                        type="button"
                        className="add-sound-modal__draft-discard"
                        onClick={handleDiscardDraft}
                        title={i18n.t("NarrationDraft.Discard") || "Taslağı at"}
                      >
                        <LuUndo2 size={13} />
                        {i18n.t("NarrationDraft.Discard") || "Taslağı at"}
                      </button>
                      {!narrationDraft.edited && (
                        <button
                          type="button"
                          className="add-sound-modal__draft-approve"
                          onClick={() => handleAddSound({ keepOpen: true })}
                          title={
                            i18n.t("NarrationDraft.ApproveHint") || "Yalnızca bu sahneye uygula"
                          }
                        >
                          <LuCheck size={13} />
                          {i18n.t("NarrationDraft.Approve") || "Onayla"}
                        </button>
                      )}
                    </span>
                  )}
                </div>
              )}
              {slideNote && slideNote !== narrationText && !isGenerating && !isDraftView && !viewingOriginal && (
                <button
                  type="button"
                  className="add-sound-modal__slide-note"
                  title={slideNote}
                  onClick={() => {
                    if (baselineTextRef.current == null) baselineTextRef.current = narrationText;
                    setNarrationText(slideNote);
                    setUserTypedText(true);
                    setAudioGeneratedInSession(false);
                    setValidationError("");
                  }}
                  style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 6, padding: "6px 8px", fontSize: 12, border: "1px dashed #cbd5e1", borderRadius: 4, background: "#f8fafc", cursor: "pointer" }}
                >
                  <strong>{i18n.t("UseSlideNotes")}</strong>
                  <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#64748b" }}>
                    {slideNote}
                  </span>
                </button>
              )}
              <textarea
                className={`add-sound-modal__ai-voice-textarea${isDraftView ? " add-sound-modal__ai-voice-textarea--draft" : ""}${viewingOriginal ? " add-sound-modal__ai-voice-textarea--original" : ""}`}
                rows={5}
                value={viewingOriginal ? stripSSML(draftOriginalText) : displayedText}
                disabled={isGenerating}
                readOnly={viewingOriginal}
                onChange={(e) => {
                  if (isGenerating || viewingOriginal) return;
                  const newValue = e.target.value;
                  // İlk düzenlemede metnin önceki hâli "orijinal" olarak saklanır.
                  if (baselineTextRef.current == null) baselineTextRef.current = narrationText;
                  setNarrationText(newValue);
                  setUserTypedText(true);
                  setAudioGeneratedInSession(false);
                  setValidationError("");
                  // Hazır taslak üzerinde düzenleme: taslak metni güncellenir, ses
                  // artık metinle uyuşmadığı için "edited" işaretlenir.
                  if (isDraftView && narrationDraft.status === "ready") {
                    setNarrationDraft(navItemSelected, { text: newValue, edited: true });
                  }
                }}
                style={isGenerating ? { opacity: 0.6, cursor: "not-allowed" } : {}}
              />
              {(() => {
                if (!userTypedText) return null;
                const est = estimateSpeechDuration(narrationText, selectedService);
                if (!est) return null;
                return (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginTop: 4,
                      display: "inline-block",
                    }}
                  >
                    {i18n.t("EstimatedVoiceDuration") || "Tahmini ses süresi"}: ~
                    {formatDurationLabel(est)}
                  </span>
                );
              })()}
            </div>
            <div className="add-sound-modal__voice-profile">
              <div className="add-sound-modal__voice-row">
                <div
                  className="add-sound-modal__voice-avatar"
                  style={genderAvatarStyle(selectedVoice?.gender)}
                >
                  {selectedVoice?.name ? selectedVoice.name[0].toUpperCase() : "?"}
                </div>
                <div className="add-sound-modal__voice-details">
                  <div className="add-sound-modal__voice-title-row">
                    <span className="add-sound-modal__voice-name">
                      {selectedVoice?.name || "-"}
                    </span>
                    {(narratorMode === "own" || selectedService) && (
                      <span className="add-sound-modal__voice-pill">
                        {narratorMode === "own"
                          ? i18n.t("OwnVoice")
                          : i18n.t(SERVICE_LABELS[selectedService] || selectedService)}
                      </span>
                    )}
                    {selectedVoice?.gender && (
                      <span className="add-sound-modal__voice-pill">
                        {normalizeGender(selectedVoice.gender) === "male"
                          ? i18n.t("Male") || "Erkek"
                          : i18n.t("Female") || "Kadın"}
                      </span>
                    )}
                    {selectedLanguage && (
                      <span className="add-sound-modal__voice-pill">{selectedLanguage}</span>
                    )}
                    {/* Kaçıncı sahnede olduğumuz — kaldırılan ai-voice-header'dan
                        buraya taşındı; oradaki diğer bilgiler bu satırda zaten var. */}
                    <span className="add-sound-modal__voice-pill">
                      {i18n.t("SceneOf", { current: sceneIndex, total: totalScenes }) ||
                        `Scene ${sceneIndex} of ${totalScenes}`}
                    </span>
                  </div>
                </div>
                {/* Üretilen anlatım sesini buradan dinle (AvatarNarrationTab ile aynı
                    tasarım). Örnek/sample ses DEĞİL; metin seçili sesle seslendirilip
                    audioUrl oluşunca aktifleşir, seslendirilmeden dinlenemez.
                    Aşağıdaki mini oynatıcıyla aynı state'i paylaşır. */}
                {selectedVoice && (
                  <button
                    type="button"
                    className="add-sound-modal__voice-card-play"
                    title={i18n.t("ListenPreview") || "Listen to the generated preview"}
                    aria-label={i18n.t("ListenPreview") || "Listen to the generated preview"}
                    onClick={handlePlayPause}
                    disabled={!audioUrl}
                  >
                    {isPlaying ? <LuPause /> : <LuPlay />}
                  </button>
                )}
                <button
                  type="button"
                  className="add-sound-modal__voice-action"
                  onClick={() =>
                    setProfileOpen((prev) => {
                      if (!prev) setValidationError("");
                      return !prev;
                    })
                  }
                  disabled={isGenerating}
                  style={isGenerating ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                >
                  {selectedVoice?.name || narratorMode === "own"
                    ? i18n.t("ChangeNarrator") || "Change narrator"
                    : i18n.t("SelectNarrator") || "Anlatıcı Seç"}
                  <span className="add-sound-modal__voice-action-icon">
                    {profileOpen ? <LuChevronUp /> : <LuChevronDown />}
                  </span>
                </button>
              </div>
              {profileOpen && (
                <>
                  <div className="add-sound-modal__voice-toggle">
                    <button
                      type="button"
                      className={`add-sound-modal__voice-toggle-btn${narratorMode === "ai" ? " add-sound-modal__voice-toggle-btn--active" : ""}`}
                      onClick={() => {
                        setNarratorMode("ai");
                        setSelectedService("");
                        setSelectedLanguage("");
                        setSelectedVoice(null);
                        setVoices([]);
                      }}
                    >
                      {i18n.t("AIVoices") || "AI Voices"}
                    </button>
                    <button
                      type="button"
                      className={`add-sound-modal__voice-toggle-btn${narratorMode === "own" ? " add-sound-modal__voice-toggle-btn--active" : ""}`}
                      onClick={() => {
                        setNarratorMode("own");
                        loadClonedVoices();
                      }}
                    >
                      {i18n.t("BringYourOwnVoice") || "Clone Voice"}
                    </button>
                  </div>

                  {narratorMode === "ai" ? (
                    <div>
                      <div className="add-sound-modal__voice-filters">
                        {/* Sağlayıcı — chip/pill (AvatarNarrationTab tasarımı) */}
                        <div className="add-sound-modal__voice-filter">
                          <div className="add-sound-modal__voice-filter-label">
                            {i18n.t("Provider") || "Sağlayıcı"}
                          </div>
                          <div className="add-sound-modal__voice-filter-chips">
                            {[
                              { value: "", label: i18n.t("AllProviders") || "All" },
                              // ElevenLabs iki ayrı entry: model seçimi buradan yapılır.
                              { value: "elevenlabs", model: "v2_5", label: "ElevenLabs 2.5" },
                              { value: "elevenlabs", model: "v3", label: "ElevenLabs 3" },
                              { value: "google", label: i18n.t("Google") || "Google" },
                              { value: "azure", label: i18n.t("Azure") || "Azure" },
                              { value: "sestek", label: i18n.t("Sestek") || "Sestek" },
                            ]
                              // "Sestek" sağlayıcı çipi gizlendi — seçenek/label silinmedi,
                              // sadece render'da filtrelendi. Geri açmak için filtreyi kaldır.
                              .filter((p) => p.value !== "sestek")
                              .map((p) => {
                                const isActive = p.model
                                  ? selectedService === p.value &&
                                    selectedElevenLabsModel === p.model
                                  : selectedService === p.value;
                                return (
                                  <button
                                    key={p.label}
                                    type="button"
                                    className={`add-sound-modal__voice-filter-chip add-sound-modal__voice-filter-chip--provider${
                                      isActive ? " add-sound-modal__voice-filter-chip--on" : ""
                                    }`}
                                    onClick={() => {
                                      // Aynı provider'da model değiştirirken sesi/voice
                                      // listesini sıfırlama — sadece model'i güncelle.
                                      if (selectedService !== p.value) {
                                        handleServiceChange(p.value);
                                      }
                                      if (p.model) setSelectedElevenLabsModel(p.model);
                                    }}
                                  >
                                    {p.label}
                                  </button>
                                );
                              })}
                          </div>
                        </div>

                        {/* Dil filtresi: "Tümü", ElevenLabs (multilingual) ve Google
                            hariç dropdown gösterilir (Google'da dil seçimi yok). */}
                        {selectedService &&
                          selectedService !== "elevenlabs" &&
                          selectedService !== "google" && (
                            <div className="add-sound-modal__voice-filter">
                              <div className="add-sound-modal__voice-filter-label">
                                {i18n.t("language") || "Language"}
                              </div>
                              <div className="add-sound-modal__voice-select-input">
                                <select
                                  value={selectedLanguage}
                                  onChange={(e) => {
                                    setSelectedLanguage(e.target.value);
                                    setCurrentPage(1);
                                    if (selectedService) {
                                      loadVoices(
                                        selectedService,
                                        e.target.value,
                                        selectedGender,
                                        1,
                                        ""
                                      );
                                    }
                                  }}
                                  disabled={!selectedService}
                                >
                                  <option value="">
                                    {i18n.t("SelectLanguage") || "Bir dil seçiniz"}
                                  </option>
                                  {getLanguageOptions().map((lang) => (
                                    <option key={lang.value} value={lang.value}>
                                      {lang.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}

                        {/* ElevenLabs model seçimi provider chip'lerinde ("ElevenLabs
                            2.5" / "ElevenLabs 3") yapılır — ayrı bir UI yok. Dil
                            backend'de course kaydından okunur. */}

                        {/* Cinsiyet — chip/pill (AvatarNarrationTab tasarımı) */}
                        <div className="add-sound-modal__voice-filter add-sound-modal__voice-filter--gender">
                          <div className="add-sound-modal__voice-filter-label">
                            {i18n.t("Gender") || "Gender"}
                          </div>
                          <div className="add-sound-modal__voice-filter-chips">
                            {[
                              { value: "", label: i18n.t("AllGenders") || "All" },
                              { value: "female", label: i18n.t("Female") || "Female" },
                              { value: "male", label: i18n.t("Male") || "Male" },
                            ].map((g) => (
                              <button
                                key={g.value || "all"}
                                type="button"
                                className={`add-sound-modal__voice-filter-chip${
                                  selectedGender === g.value
                                    ? " add-sound-modal__voice-filter-chip--on"
                                    : ""
                                }`}
                                onClick={() => {
                                  setSelectedGender(g.value);
                                  setCurrentPage(1);
                                  // "Tümü" (All) dahil her durumda cinsiyeti gönder
                                  // (selectedService "" ise service paramı eklenmez).
                                  loadVoices(selectedService, selectedLanguage, g.value, 1, "");
                                }}
                              >
                                {g.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {false && selectedService === "google" && (
                        <div className="add-sound-modal__advanced-settings">
                          <div
                            role="button"
                            tabIndex={0}
                            className="add-sound-modal__advanced-settings-toggle"
                            onClick={() => setShowAdvancedSettings((prev) => !prev)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setShowAdvancedSettings((prev) => !prev);
                              }
                            }}
                          >
                            <span className="add-sound-modal__advanced-settings-title">
                              {translateOrFallback("AdvancedSettings", "Gelişmiş Ayarlar")}
                              <span
                                style={{
                                  position: "relative",
                                  display: "inline-flex",
                                  cursor: "help",
                                  marginLeft: 8,
                                }}
                                onMouseEnter={() => setShowGeminiGuide(true)}
                                onMouseLeave={() => setShowGeminiGuide(false)}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <LuInfo size={16} style={{ color: "#4a90e2" }} />
                                {showGeminiGuide && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "calc(100% + 8px)",
                                      left: 0,
                                      zIndex: 1000,
                                      background: "#fff",
                                      color: "#222",
                                      border: "1px solid #d0d7de",
                                      borderRadius: 8,
                                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                                      padding: 12,
                                      width: 520,
                                      maxWidth: "90vw",
                                      fontSize: 12,
                                      lineHeight: 1.45,
                                      textAlign: "left",
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
                                      🚀{" "}
                                      {translateOrFallback(
                                        "GeminiGuideTitle",
                                        "Gemini TTS Ses Yönetmeni Rehberi"
                                      )}
                                    </div>
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        marginBottom: 10,
                                      }}
                                    >
                                      <thead>
                                        <tr style={{ background: "#f6f8fa" }}>
                                          <th
                                            style={{
                                              padding: "6px 8px",
                                              borderBottom: "1px solid #d0d7de",
                                              textAlign: "left",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {translateOrFallback(
                                              "GeminiGuideColType",
                                              "Özellik Türü"
                                            )}
                                          </th>
                                          <th
                                            style={{
                                              padding: "6px 8px",
                                              borderBottom: "1px solid #d0d7de",
                                              textAlign: "left",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {translateOrFallback(
                                              "GeminiGuideColCommand",
                                              "Komut (Etiket)"
                                            )}
                                          </th>
                                          <th
                                            style={{
                                              padding: "6px 8px",
                                              borderBottom: "1px solid #d0d7de",
                                              textAlign: "left",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {translateOrFallback(
                                              "GeminiGuideColEffect",
                                              "Ne Yapar?"
                                            )}
                                          </th>
                                          <th
                                            style={{
                                              padding: "6px 8px",
                                              borderBottom: "1px solid #d0d7de",
                                              textAlign: "left",
                                              fontWeight: 600,
                                            }}
                                          >
                                            {translateOrFallback(
                                              "GeminiGuideColExample",
                                              "Örnek Kullanım"
                                            )}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {GEMINI_FLASH3_DIRECTIVES.map((d) => (
                                          <tr key={d.tag}>
                                            <td
                                              style={{
                                                padding: "6px 8px",
                                                borderBottom: "1px solid #eaeef2",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {d.icon} {translateOrFallback(d.typeKey, d.type)}
                                            </td>
                                            <td
                                              style={{
                                                padding: "6px 8px",
                                                borderBottom: "1px solid #eaeef2",
                                                fontFamily: "monospace",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {d.tag}
                                            </td>
                                            <td
                                              style={{
                                                padding: "6px 8px",
                                                borderBottom: "1px solid #eaeef2",
                                              }}
                                            >
                                              {translateOrFallback(d.effectKey, d.effect)}
                                            </td>
                                            <td
                                              style={{
                                                padding: "6px 8px",
                                                borderBottom: "1px solid #eaeef2",
                                                fontFamily: "monospace",
                                              }}
                                            >
                                              {d.example}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                      {translateOrFallback(
                                        "GeminiGuideExampleText",
                                        "Örnek Metin:"
                                      )}
                                    </div>
                                    <div
                                      style={{
                                        background: "#f6f8fa",
                                        border: "1px solid #eaeef2",
                                        borderRadius: 6,
                                        padding: 8,
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {GEMINI_FLASH3_EXAMPLE_TEXT}
                                    </div>
                                  </div>
                                )}
                              </span>
                            </span>
                            {showAdvancedSettings ? <LuChevronUp /> : <LuChevronDown />}
                          </div>
                          {showAdvancedSettings && (
                            <div className="add-sound-modal__advanced-settings-body">
                              <div className="add-sound-modal__voice-select">
                                <div className="add-sound-modal__voice-select-label">
                                  {translateOrFallback("GeminiStyleLabel", "Stil")}
                                </div>
                                <div className="add-sound-modal__voice-select-input">
                                  <select
                                    value={geminiStyle}
                                    onChange={(e) => setGeminiStyle(e.target.value)}
                                  >
                                    <option value="">
                                      {translateOrFallback("DefaultOption", "Varsayılan")}
                                    </option>
                                    {GEMINI_TTS_STYLE_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {translateOrFallback(opt.labelKey, opt.fallback)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="add-sound-modal__voice-select">
                                <div className="add-sound-modal__voice-select-label">
                                  {translateOrFallback("GeminiPaceLabel", "Tempo")}
                                </div>
                                <div className="add-sound-modal__voice-select-input">
                                  <select
                                    value={geminiPace}
                                    onChange={(e) => setGeminiPace(e.target.value)}
                                  >
                                    <option value="">
                                      {translateOrFallback("DefaultOption", "Varsayılan")}
                                    </option>
                                    {GEMINI_TTS_PACE_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {translateOrFallback(opt.labelKey, opt.fallback)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="add-sound-modal__voice-select">
                                <div className="add-sound-modal__voice-select-label">
                                  {translateOrFallback("AudioProfileLabel", "Ses Profili")}
                                </div>
                                <div className="add-sound-modal__voice-select-input">
                                  <input
                                    type="text"
                                    value={geminiAudioProfile}
                                    onChange={(e) => setGeminiAudioProfile(e.target.value)}
                                    placeholder={translateOrFallback(
                                      "AudioProfilePlaceholder",
                                      "ör. sıcak anlatıcı, stüdyo podcasti"
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {(selectedService || isLoadingVoices || voices.length > 0) && (
                        <>
                          <div className="add-sound-modal__voice-list-header">
                            <span className="add-sound-modal__voice-list-title">
                              {i18n.t("Voices") || "Sesler"}
                            </span>

                            {/* TODO: Delete this Deprecated  */}
                            {/* {voices.length > 0 &&
                        voices.some(
                          (v) =>
                            v.sampleUrlsByLanguage && Object.keys(v.sampleUrlsByLanguage).length > 0
                        ) && (
                          <select
                            className="add-sound-modal__voice-list-lang-select"
                            value={sampleLanguage}
                            onChange={(e) => setSampleLanguage(e.target.value)}
                          >
                            <option value="">
                              {i18n.t("SampleLanguage") || "Sample Language"}
                            </option>
                            {Array.from(
                              new Set(
                                voices
                                  .filter((v) => v.sampleUrlsByLanguage)
                                  .flatMap((v) => Object.keys(v.sampleUrlsByLanguage))
                              )
                            )
                              .sort()
                              .map((lang) => (
                                <option key={lang} value={lang}>
                                  {lang}
                                </option>
                              ))}
                          </select>
                        )} */}
                          </div>

                          <div className="add-sound-modal__voice-list">
                            {isLoadingVoices ? (
                              <div className="add-sound-modal__loading">
                                {i18n.t("Loading") || "Loading..."}
                              </div>
                            ) : (
                              <>
                                {voices.map((voice) => {
                                  const availableLanguages = voice.sampleUrlsByLanguage
                                    ? Object.keys(voice.sampleUrlsByLanguage)
                                    : [];
                                  const hasSample =
                                    availableLanguages.length > 0 ||
                                    voice.sampleUrl ||
                                    voice.audioSampleUrl ||
                                    voice.previewUrl;

                                  const isActive = selectedVoice?.id === voice.id;
                                  return (
                                    <div
                                      key={voice.id}
                                      className="add-sound-modal__voice-card-wrapper"
                                    >
                                      <div
                                        className={`add-sound-modal__voice-card${
                                          isActive ? " add-sound-modal__voice-card--active" : ""
                                        }`}
                                        onClick={() => {
                                          setSelectedVoice(voice);

                                          // "Tümü" (All) filtresindeyken provider boş; üretim
                                          // için seçilen sesin gerçek sağlayıcısını set et.
                                          if (!selectedService) {
                                            const svc = voiceToService(voice);
                                            if (svc) setSelectedService(svc);
                                          }

                                          // Auto-select language and gender from voice metadata
                                          if (voice.language) {
                                            setSelectedLanguage(
                                              normalizeLanguageCode(voice.language)
                                            );
                                          }
                                          if (voice.gender) {
                                            setSelectedGender(normalizeGender(voice.gender));
                                          }
                                        }}
                                        style={{ cursor: "pointer" }}
                                      >
                                        {/* Sample oynat — cinsiyete göre renkli (AvatarNarrationTab) */}
                                        <button
                                          type="button"
                                          className="add-sound-modal__voice-card-play"
                                          style={genderAvatarStyle(voice.gender)}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSamplePlay(voice);
                                          }}
                                          disabled={!hasSample}
                                        >
                                          {samplePlayingId === voice.id ? <LuPause /> : <LuPlay />}
                                        </button>
                                        <div className="add-sound-modal__voice-card-body">
                                          <div className="add-sound-modal__voice-card-name">
                                            {voice.name}
                                          </div>
                                          <div className="add-sound-modal__voice-card-meta">
                                            {voice.gender && (
                                              <span>
                                                {normalizeGender(voice.gender) === "male"
                                                  ? i18n.t("Male") || "Erkek"
                                                  : i18n.t("Female") || "Kadın"}
                                              </span>
                                            )}
                                            {voice.servicename && <span>{voice.servicename}</span>}
                                          </div>
                                        </div>
                                        {isActive && (
                                          <span className="add-sound-modal__voice-card-check">
                                            <LuCheck size={14} />
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                {totalPages > 1 && (
                                  <div className="add-sound-modal__voice-pagination">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                      (page) => (
                                        <button
                                          key={page}
                                          type="button"
                                          className={`add-sound-modal__voice-pagination-btn${currentPage === page ? " add-sound-modal__voice-pagination-btn--active" : ""}`}
                                          onClick={() => {
                                            setCurrentPage(page);
                                            loadVoices(
                                              selectedService,
                                              selectedLanguage,
                                              selectedGender,
                                              page,
                                              ""
                                            );
                                          }}
                                        >
                                          {page}
                                        </button>
                                      )
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    // Own Voice (Cloned Voices) Section
                    <div className="add-sound-modal__cloned-voices-section">
                      <div className="add-sound-modal__clone-upload-card">
                        <h4 className="add-sound-modal__clone-upload-title">
                          {i18n.t("CloneYourVoice") || "Clone Your Voice"}
                        </h4>
                        <p className="add-sound-modal__clone-upload-description">
                          {i18n.t("CloneVoiceDescription") ||
                            "Upload an audio sample to clone your voice"}
                        </p>

                        {/* Upload Mode Toggle */}
                        <div className="add-sound-modal__upload-mode-toggle">
                          <button
                            type="button"
                            className={`add-sound-modal__upload-mode-btn${uploadMode === "file" ? " add-sound-modal__upload-mode-btn--active" : ""}`}
                            onClick={() => {
                              setUploadMode("file");
                              resetRecording();
                            }}
                          >
                            {i18n.t("UploadVoiceFile") || "Upload Voice File"}
                          </button>
                          <button
                            type="button"
                            className={`add-sound-modal__upload-mode-btn${uploadMode === "microphone" ? " add-sound-modal__upload-mode-btn--active" : ""}`}
                            onClick={() => {
                              setUploadMode("microphone");
                              if (cloneVoiceFilePreviewUrl) {
                                URL.revokeObjectURL(cloneVoiceFilePreviewUrl);
                                setCloneVoiceFilePreviewUrl(null);
                              }
                              setCloneVoiceFile(null);
                            }}
                          >
                            {i18n.t("RecordWithMicrophone") || "Record with Microphone"}
                          </button>
                        </div>

                        <div className="add-sound-modal__clone-upload-form">
                          <div className="add-sound-modal__clone-form-row">
                            <div className="add-sound-modal__clone-form-field">
                              <label className="add-sound-modal__clone-form-label">
                                {i18n.t("VoiceName") || "Voice Name"} *
                              </label>
                              <input
                                type="text"
                                className="add-sound-modal__clone-form-input"
                                placeholder={i18n.t("EnterVoiceName") || "Enter voice name"}
                                value={cloneVoiceName}
                                onChange={(e) => setCloneVoiceName(e.target.value)}
                              />
                            </div>
                            <div className="add-sound-modal__clone-form-field">
                              <label className="add-sound-modal__clone-form-label">
                                {i18n.t("Gender") || "Gender"} *
                              </label>
                              <select
                                className="add-sound-modal__clone-form-select"
                                value={cloneVoiceGender}
                                onChange={(e) => setCloneVoiceGender(e.target.value)}
                              >
                                <option value="" disabled>
                                  {i18n.t("SelectGender") || "Select Gender"}
                                </option>
                                <option value="male">{i18n.t("Male") || "Erkek"}</option>
                                <option value="female">{i18n.t("Female") || "Kadın"}</option>
                              </select>
                            </div>
                          </div>

                          {uploadMode === "file" ? (
                            // File Upload Mode
                            <div className="add-sound-modal__clone-form-field">
                              <label className="add-sound-modal__clone-form-label">
                                {i18n.t("AudioFile") || "Audio File"} *
                              </label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="audio/mpeg,audio/mp3,audio/wav,audio/wave,audio/x-wav,audio/ogg,audio/webm,audio/aac,audio/m4a,audio/x-m4a,audio/flac,audio/mp4,.mp3,.wav,.ogg,.webm,.m4a,.aac,.flac"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    handleFileSelect(e.target.files[0]);
                                  }
                                }}
                              />
                              <div
                                className={`add-sound-modal__clone-file-dropzone ${isDragging ? "add-sound-modal__clone-file-dropzone--dragging" : ""} ${cloneVoiceFile ? "add-sound-modal__clone-file-dropzone--has-file" : ""}`}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                  border:
                                    "2px dashed " +
                                    (isDragging ? "#007bff" : cloneVoiceFile ? "#28a745" : "#ccc"),
                                  borderRadius: "8px",
                                  padding: "20px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  backgroundColor: isDragging
                                    ? "#f0f8ff"
                                    : cloneVoiceFile
                                      ? "#f0fff4"
                                      : "#fafafa",
                                  transition: "all 0.3s ease",
                                  marginTop: "8px",
                                  minHeight: "80px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                {cloneVoiceFile ? (
                                  <>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color: "#333",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {cloneVoiceFile.name}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        marginBottom: "16px",
                                      }}
                                    >
                                      {(cloneVoiceFile.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                    {cloneVoiceFilePreviewUrl && (
                                      <audio
                                        controls
                                        src={cloneVoiceFilePreviewUrl}
                                        style={{
                                          width: "100%",
                                          marginBottom: "12px",
                                        }}
                                      />
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (cloneVoiceFilePreviewUrl) {
                                          URL.revokeObjectURL(cloneVoiceFilePreviewUrl);
                                        }
                                        setCloneVoiceFile(null);
                                        setCloneVoiceFilePreviewUrl(null);
                                        if (fileInputRef.current) {
                                          fileInputRef.current.value = "";
                                        }
                                      }}
                                      style={{
                                        padding: "8px 20px",
                                        fontSize: "13px",
                                        borderRadius: "4px",
                                        border: "1px solid #dc3545",
                                        backgroundColor: "#fff",
                                        color: "#dc3545",
                                        cursor: "pointer",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {i18n.t("AddSoundUpload.Remove") || "Remove"}
                                    </button>
                                  </>
                                ) : (
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      color: "#666",
                                    }}
                                  >
                                    {isDragging
                                      ? i18n.t("DropFileHere") || "Drop your audio file here"
                                      : i18n.t("DragDropOrClick") ||
                                        "Drag & drop audio file or click to browse"}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            // Microphone Recording Mode
                            <div className="add-sound-modal__recording-section">
                              {!isRecording && !recordingBlob && (
                                <div className="add-sound-modal__recording-instruction">
                                  <p className="add-sound-modal__recording-instruction-text">
                                    {i18n.t("RecordingInstruction") ||
                                      "Click 'Start Recording' button to begin recording your voice sample."}
                                  </p>
                                </div>
                              )}

                              {isRecording && randomRecordingTextIndex !== null && (
                                <div className="add-sound-modal__recording-prompt">
                                  <div className="add-sound-modal__recording-text-box">
                                    <p className="add-sound-modal__recording-text-label">
                                      {i18n.t("ReadThisText") || "Read this text:"}
                                    </p>
                                    <p className="add-sound-modal__recording-text-content">
                                      {recordingTexts[randomRecordingTextIndex]}
                                    </p>
                                  </div>
                                </div>
                              )}

                              <div className="add-sound-modal__recording-controls">
                                {!isRecording && !recordingBlob && (
                                  <button
                                    type="button"
                                    className="add-sound-modal__recording-btn add-sound-modal__recording-btn--start"
                                    onClick={startMicrophoneRecording}
                                  >
                                    {i18n.t("StartRecording") || "Start Recording"}
                                  </button>
                                )}

                                {isRecording && (
                                  <button
                                    type="button"
                                    className="add-sound-modal__recording-btn add-sound-modal__recording-btn--stop"
                                    onClick={stopMicrophoneRecording}
                                  >
                                    {i18n.t("StopRecording") || "Stop Recording"}
                                  </button>
                                )}

                                {recordingBlob && !isRecording && (
                                  <button
                                    type="button"
                                    className="add-sound-modal__recording-btn add-sound-modal__recording-btn--reset"
                                    onClick={resetRecording}
                                  >
                                    {i18n.t("ResetRecording") || "Reset Recording"}
                                  </button>
                                )}
                              </div>

                              {isRecording && (
                                <div className="add-sound-modal__recording-indicator">
                                  <span className="add-sound-modal__recording-indicator-dot"></span>
                                  <span className="add-sound-modal__recording-indicator-text">
                                    {i18n.t("Recording") || "Recording..."}
                                  </span>
                                </div>
                              )}

                              {recordingBlob && recordingAudioUrl && !isRecording && (
                                <div className="add-sound-modal__recording-playback">
                                  <p className="add-sound-modal__recording-playback-label">
                                    {i18n.t("PlayRecording") || "Play Recording:"}
                                  </p>
                                  <audio
                                    controls
                                    src={recordingAudioUrl}
                                    className="add-sound-modal__recording-audio-player"
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            type="button"
                            className="add-sound-modal__clone-upload-btn"
                            disabled={
                              !cloneVoiceName ||
                              !cloneVoiceGender ||
                              (uploadMode === "file" && !cloneVoiceFile) ||
                              (uploadMode === "microphone" && !recordingBlob) ||
                              isUploadingVoice
                            }
                            onClick={async () => {
                              if (cloneVoiceName) {
                                const fileToUpload =
                                  uploadMode === "file"
                                    ? cloneVoiceFile
                                    : new File([recordingBlob], `${cloneVoiceName}.wav`, {
                                        type: "audio/wav",
                                      });
                                const result = await cloneVoice(
                                  fileToUpload,
                                  cloneVoiceName,
                                  cloneVoiceGender
                                );
                                if (result) {
                                  setCloneVoiceName("");
                                  setCloneVoiceFile(null);
                                  setCloneVoiceGender("");
                                  if (cloneVoiceFilePreviewUrl) {
                                    URL.revokeObjectURL(cloneVoiceFilePreviewUrl);
                                    setCloneVoiceFilePreviewUrl(null);
                                  }
                                  resetRecording();
                                  if (fileInputRef.current) {
                                    fileInputRef.current.value = "";
                                  }
                                  loadClonedVoices();
                                }
                              }
                            }}
                          >
                            {isUploadingVoice
                              ? i18n.t("Uploading") || "Uploading..."
                              : i18n.t("CloneVoice") || "Clone Voice"}
                          </button>
                        </div>
                      </div>

                      <div className="add-sound-modal__voice-list-header">
                        <span className="add-sound-modal__voice-list-title">
                          {i18n.t("YourClonedVoices") || "Your Cloned Voices"}
                        </span>
                      </div>

                      <div className="add-sound-modal__voice-list">
                        {isLoadingClonedVoices ? (
                          <div className="add-sound-modal__loading">
                            {i18n.t("Loading") || "Loading"}...
                          </div>
                        ) : clonedVoices.length === 0 ? (
                          <div className="add-sound-modal__voice-empty">
                            {i18n.t("NoClonedVoices") ||
                              "No cloned voices yet. Upload an audio sample to clone your voice."}
                          </div>
                        ) : (
                          <>
                            {clonedVoices.map((voice) => {
                              const sampleUrl = voice.sampleUrlsByLanguage?.sample?.v1 || null;

                              const isActive = selectedVoice?.id === voice.id;
                              return (
                                <div key={voice.id} className="add-sound-modal__voice-card-wrapper">
                                  <div
                                    className={`add-sound-modal__voice-card add-sound-modal__voice-card--deletable${
                                      isActive ? " add-sound-modal__voice-card--active" : ""
                                    }`}
                                    onClick={() => {
                                      setSelectedVoice(voice);
                                    }}
                                    style={{ cursor: "pointer" }}
                                  >
                                    {/* Sağ üst çöp kutusu — kart hover/focus olunca görünür.
                                        stopPropagation → kartı seçmez. */}
                                    <span
                                      className="add-sound-modal__voice-card-delete-hover"
                                      role="button"
                                      tabIndex={0}
                                      aria-label={i18n.t("VoiceDelete.Button") || "Sesi sil"}
                                      title={i18n.t("VoiceDelete.Button") || "Sesi sil"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setVoiceToDelete(voice);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setVoiceToDelete(voice);
                                        }
                                      }}
                                    >
                                      <LuTrash2 size={13} />
                                    </span>
                                    {/* Sample oynat — cinsiyete göre renkli (AvatarNarrationTab) */}
                                    <button
                                      type="button"
                                      className="add-sound-modal__voice-card-play"
                                      style={genderAvatarStyle(voice.gender)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSamplePlay(voice);
                                      }}
                                      disabled={!sampleUrl}
                                    >
                                      {samplePlayingId === voice.id ? <LuPause /> : <LuPlay />}
                                    </button>
                                    <div className="add-sound-modal__voice-card-body">
                                      <div className="add-sound-modal__voice-card-name">
                                        {voice.name}
                                      </div>
                                      <div className="add-sound-modal__voice-card-meta">
                                        {voice.gender && (
                                          <span>
                                            {normalizeGender(voice.gender) === "male"
                                              ? i18n.t("Male") || "Erkek"
                                              : i18n.t("Female") || "Kadın"}
                                          </span>
                                        )}
                                        {voice.language && <span>{voice.language}</span>}
                                      </div>
                                    </div>
                                    {isActive && (
                                      <span className="add-sound-modal__voice-card-check">
                                        <LuCheck size={14} />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* "Ses Oluştur / Yeniden Oluştur" — anlatıcı profili kutusunun en
                  altında. Metin varsa kutu kapalıyken de görünür: metin değiştirildikten
                  sonra kullanıcı butonu arayamaz olmasın. Yalnızca hiç metin yokken
                  (boş, yeni anlatım) gizli. */}
              {(profileOpen || narrationText.trim().length > 0) && (
                <button
                  type="button"
                  className="add-sound-modal__ai-voice-regenerate"
                  onClick={generateTTS}
                  disabled={isGenerating || !narratorSelected}
                  title={!narratorSelected ? i18n.t("PleaseSelectNarrator") : undefined}
                  style={
                    !isGenerating && !narratorSelected
                      ? { opacity: 0.5, cursor: "not-allowed" }
                      : undefined
                  }
                >
                  {isGenerating
                    ? i18n.t("Generating") || "Oluşturuluyor..."
                    : audioUrl
                      ? i18n.t("RegenerateVoice") || "Yeniden Oluştur"
                      : i18n.t("GenerateVoice") || "Ses Oluştur"}
                </button>
              )}
              {/* Butonun aksine hata mesajı daraltılmış halde de görünür: "önce ses
                  oluşturun" uyarısı kaydetmeye basıldığında buradan gelir. */}
              {validationError && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>{validationError}</span>
              )}
            </div>
            <audio
              ref={audioRef}
              src={audioUrl || ""}
              preload="metadata"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleAudioEnded}
              style={{ display: "none" }}
            />
            {audioUrl && (
              <div className="add-sound-modal__ai-voice-player">
                <button
                  type="button"
                  className="add-sound-modal__ai-voice-play"
                  onClick={handlePlayPause}
                >
                  {isPlaying ? <LuPause /> : <LuPlay />}
                </button>
                <div
                  className="add-sound-modal__ai-voice-bar"
                  onClick={handleProgressBarClick}
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className="add-sound-modal__ai-voice-bar-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="add-sound-modal__ai-voice-time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <button
                  type="button"
                  className={`add-sound-modal__ai-voice-settings-btn${soundSettingsOpen ? " add-sound-modal__ai-voice-settings-btn--active" : ""}`}
                  onClick={() => setSoundSettingsOpen((prev) => !prev)}
                  title={i18n.t("AddSoundUpload.SoundSettings") || "Ses Ayarları"}
                >
                  <LuSettings />
                </button>
              </div>
            )}

            {audioUrl && soundSettingsOpen && (
              <div className="add-sound-modal__upload-settings" style={{ marginTop: "12px" }}>
                <div
                  className="add-sound-modal__upload-settings-grid"
                  style={{ gridTemplateColumns: "1fr" }}
                >
                  {/* Başlangıç Zamanı alanı gizlendi (AI Anlatım sesi) */}
                  {false && (
                    <div className="add-sound-modal__upload-settings-field">
                      <label>{i18n.t("AddSoundUpload.StartTime") || "Start Time"}</label>
                      <input
                        type="number"
                        value={sceneStartTime}
                        onChange={(event) => {
                          const newStart = Number(event.target.value) || 0;
                          setSceneStartTime(newStart);
                          setSceneEndTime(newStart + duration);
                        }}
                      />
                    </div>
                  )}
                  <div className="add-sound-modal__upload-settings-field">
                    <div className="add-sound-modal__upload-settings-row">
                      <label>{i18n.t("AddSoundUpload.Volume") || "Volume"}</label>
                      <span>{volume}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      onChange={(event) => {
                        const v = Number(event.target.value);
                        setVolume(v);
                        if (isDraftView)
                          setNarrationDraft(navItemSelected, { volumeLevel: v / 100 });
                      }}
                      className="add-sound-modal__upload-settings-range"
                      style={{
                        background: `linear-gradient(90deg, #6d5dfc ${volume}%, #e2e8f0 ${volume}%)`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <NarrateAllConfirmModal
          show={confirmAllOpen}
          onClose={() => {
            setConfirmAllOpen(false);
            pendingSoundDataRef.current = null;
          }}
          onConfirm={handleApplyToAllScenes}
          sceneCount={totalScenes}
          busy={isNarratingAll}
        />

        <DeleteAvatarConfirmModal
          show={!!voiceToDelete}
          onClose={() => setVoiceToDelete(null)}
          onConfirm={confirmDeleteClonedVoice}
          busy={isDeletingVoice}
          title={i18n.t("VoiceDelete.Title") || "Sesi sil"}
          description={
            i18n.t("VoiceDelete.Desc", {
              name: voiceToDelete?.name || "",
              interpolation: { escapeValue: false },
            }) ||
            `Emin misiniz? Klonlanmış sesiniz „${voiceToDelete?.name || ""}“ silinecek. Bu işlem geri alınamaz. Sahnelere daha önce eklenmiş seslendirmeler etkilenmez.`
          }
        />
      </>
    );
  }
);

export default AIVoiceTab;
